/**
 * MastermindCommunityHub
 * Exacte nabouw van de originele mastermind hub — inclusief alle Firebase-koppelingen.
 *
 * Tabs: Sessies | Groei | Agenda | Instellingen
 * Header: Cover (h/v verschuifbaar) · Logo · Naam · 4 knoppen
 * Leden: slide-in panel (toggle)
 * Next meeting: popup kaart
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { auth, db, storage } from '../../firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { compressForCover } from '../../utils/imageCompressor';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useEntityContext } from '../../hooks/useEntityContext';
import { EntitySwitcher } from '../roles/EntitySwitcher';
import { networkClubService } from '../../services/networkClubService';
import { NetworkClub } from '../../types/networkclub';
import {
  collection, query, where, getDocs, doc, getDoc, updateDoc,
  setDoc, deleteDoc, onSnapshot, orderBy, serverTimestamp, limit, Timestamp, deleteField,
} from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { normalizeNetworkClubWebsiteUrl } from '../../utils/networkClubWebsite';
import { openExternalUrl } from '../../utils/openExternalUrl';
import { generateClubSlug } from '../../utils/clubSlug';
import { Camera, Users, Loader, X, ChevronDown, ChevronRight } from '../icons';
import {
  CalendarIcon, SettingsIcon as PhosphorGear, AssistantIcon, EditIcon, ProfileIcon,
  CommunityIcon, ClipboardIcon, SaveDiskIcon, DeleteIcon, MicrophoneIcon, MailIcon,
  TagIcon, FolderIcon, GrowthIcon, GoalIcon, TimeIcon, RelationIcon, ClockIcon,
  ImageIcon, CheckIcon, DownloadIcon, FullscreenIcon, EndCallIcon, VideoIcon, LaunchIcon,
} from '../icons/phosphorSystem';
import NetworkClubMeeting from './NetworkClubMeeting';
import { ClubInviteMembersModal, type ClubInviteMemberRow } from './ClubInviteMembersModal';
import ClubMeetingPlanner from './components/ClubMeetingPlanner';
import MeetingCountdown from './components/MeetingCountdown';
import PreMeetingSetupPanel from './components/PreMeetingSetupPanel';
import { PreMeetingAgendaPointsPanel } from './components/PreMeetingAgendaPointsPanel';
import { PostSessionModule } from '../../modules/structuredGrowth';
import {
  GoalEngine, CommitmentEngine, PreSessionModule,
  GroupHealthIndicator,
} from '../../modules/structuredGrowth';
import type { StructuredGrowthEntityType } from '../../modules/structuredGrowth';
import { sharedAgendaItemVisibleInMeeting } from '../../lib/sharedAgendaVisibility';
import { resolveHubCapabilities } from '../../types/entityMemberCapabilities';

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens — precies gematcht aan screenshots
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  bg:          '#FAFAF9', // warm shell (stone-50), matches Agenda/dashboard neutrals
  white:       '#FFFFFF',
  border:      '#E5E5EA',
  borderLight: '#F0F0F0',
  text:        '#1C1C1E',
  textSub:     '#6B6B6B',
  textMuted:   '#ABABAB',
  accent:      '#8DA8A0',     // sage-400
  accentLight: '#e8f0ed',     // sage-100
  accentBg:    '#e8f0ed',     // sage-100
  badgeDark:   '#1C1C1E',     // Organisator badge (donker)
  badgeLight:  '#F0F0F0',     // Lid badge (licht)
  green:       '#8DA8A0',     // sage-400
  red:         '#EF4444',
  shadow:      '0 2px 12px rgba(0,0,0,0.07)',
  shadowSm:    '0 1px 4px rgba(0,0,0,0.06)',
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface SessionRecording {
  id: string;
  roomName: string;
  entityId: string;
  downloadURL?: string;
  durationSeconds?: number;
  participants?: Array<{ displayName: string; avatarUrl?: string | null }>;
  createdAt: Date;
  summary?: string;
  bulletPoints?: string[];
  status?: string;
  actiepunten?: string[];
  beslissingen?: string[];
  leerpunten?: string[];
  aiAnalysis?: Record<string, unknown>;
  transcript?: string;
  reflectionId?: string;
}

type MemberWithMeta = ClubInviteMemberRow;

const ROLE_SORT: Record<string, number> = { owner: 0, board: 1, organizer: 2, va: 3, member: 4 };

const COUNTRY_FLAGS: Record<string, string> = {
  'Nederland': '🇳🇱', 'België': '🇧🇪', 'Duitsland': '🇩🇪', 'Frankrijk': '🇫🇷',
  'Verenigd Koninkrijk': '🇬🇧', 'Spanje': '🇪🇸', 'Italië': '🇮🇹', 'Zwitserland': '🇨🇭',
  'Denemarken': '🇩🇰', 'Zweden': '🇸🇪', 'Noorwegen': '🇳🇴', 'Portugal': '🇵🇹',
  'Griekenland': '🇬🇷', 'Ierland': '🇮🇪', 'Luxemburg': '🇱🇺', 'Finland': '🇫🇮',
  'Polen': '🇵🇱', 'Tsjechië': '🇨🇿', 'Hongarije': '🇭🇺', 'Oostenrijk': '🇦🇹',
  'Suriname': '🇸🇷', 'Marokko': '🇲🇦', 'Turkije': '🇹🇷', 'Andere': '🌍',
  'Verenigde Staten': '🇺🇸', 'Canada': '🇨🇦', 'Australië': '🇦🇺',
};

function getGmtOffset(): string {
  try {
    const parts = new Intl.DateTimeFormat('nl', { timeZoneName: 'shortOffset' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || 'GMT+1';
  } catch { return 'GMT+1'; }
}

function formatDuration(secs?: number): string {
  if (!secs) return '0:00';
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function fmtDateLong(d: Date): string {
  return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

function localTime(d: Date): string {
  return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline styles helper
// ─────────────────────────────────────────────────────────────────────────────
const S = {
  card: {
    background: T.white,
    borderRadius: 14,
    border: `1px solid ${T.border}`,
    boxShadow: T.shadowSm,
  } as React.CSSProperties,
  headerBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    height: 34, padding: '0 14px', borderRadius: 20,
    background: T.white, border: `1px solid ${T.border}`,
    fontSize: 13, fontWeight: 600, color: T.text,
    cursor: 'pointer', whiteSpace: 'nowrap' as const,
    transition: 'background 0.15s',
  } as React.CSSProperties,
  accentBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    height: 34, padding: '0 16px', borderRadius: 20,
    background: T.accent, border: 'none',
    fontSize: 13, fontWeight: 700, color: '#fff',
    cursor: 'pointer',
  } as React.CSSProperties,
};

// ─────────────────────────────────────────────────────────────────────────────
// Accordion Row (Session detail)
// ─────────────────────────────────────────────────────────────────────────────
const AccordionRow: React.FC<{
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ icon, title, subtitle, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        {icon && <span style={{ fontSize: 15, flexShrink: 0 }}>{icon}</span>}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: T.text }}>{title}</div>
          {subtitle && <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 1 }}>{subtitle}</div>}
        </div>
        {open
          ? <ChevronDown size={15} strokeWidth={2} color={T.textMuted} />
          : <ChevronRight size={15} strokeWidth={2} color={T.textMuted} />
        }
      </button>
      {open && <div style={{ padding: '0 18px 16px' }}>{children}</div>}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Private Notes Component
// ─────────────────────────────────────────────────────────────────────────────
const PrivateNotes: React.FC<{ recordingId: string; userId: string }> = ({ recordingId, userId }) => {
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const docId = `${userId}_${recordingId}`;

  useEffect(() => {
    getDoc(doc(db, 'sessionPrivateNotes', docId)).then(snap => {
      if (snap.exists()) setNotes((snap.data() as any).notes || '');
    }).catch(() => {});
  }, [docId]);

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'sessionPrivateNotes', docId), { notes, userId, recordingId, updatedAt: serverTimestamp() }, { merge: true });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, padding: '6px 10px', background: T.bg, borderRadius: 8, fontSize: 12, color: T.textSub }}>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, color: T.text, fontSize: 12 }}>B</button>
        <span style={{ color: T.border }}>/</span>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, fontStyle: 'italic', color: T.text }}>I</button>
        <span style={{ color: T.border }}>/</span>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, textDecoration: 'underline', color: T.text }}>U</button>
        <span style={{ color: T.border }}>•</span>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: T.text }}>• Lijst</button>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: T.text }}>1. Lijst</button>
      </div>
      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Schrijf hier je persoonlijke inzichten, leerpunten en acties..."
        rows={5}
        style={{ width: '100%', borderRadius: 10, border: `1px solid ${T.border}`, padding: '10px 12px', fontSize: 13, outline: 'none', resize: 'vertical', color: T.text, background: T.white, boxSizing: 'border-box', lineHeight: 1.55 }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={save} disabled={saving}
          style={{ ...S.accentBtn, background: saved ? T.green : T.accent, fontSize: 12 }}>
          {saved ? <><CheckIcon size={13} weight="bold" /> Opgeslagen</> : saving ? 'Opslaan…' : 'Notitie opslaan'}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Mijn agenda punten (sessie-detail — zelfde scope als live meeting-sidebar)
// ─────────────────────────────────────────────────────────────────────────────
const SessionMyAgendaPoints: React.FC<{ entityId: string; roomName: string; userId: string }> = ({
  entityId,
  roomName,
  userId,
}) => {
  const [items, setItems] = useState<Array<{ id: string; title: string }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId || !roomName || !userId) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const q = query(collection(db, 'sharedAgendaItems'), where('entityId', '==', entityId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (cancelled) return;
        const eventKey = roomName;
        const rows = snap.docs
          .map((d) => ({ id: d.id, data: d.data() as Record<string, unknown> }))
          .filter(({ data }) => sharedAgendaItemVisibleInMeeting(data, { entityId, roomName, eventId: eventKey }))
          .filter(({ data }) => data.submittedByUid === userId)
          .map(({ id, data }) => ({
            id,
            title: (data.title as string) || '',
            order: typeof data.order === 'number' ? data.order : 0,
            createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() ?? new Date(0),
          }))
          .sort((a, b) => a.order - b.order || a.createdAt.getTime() - b.createdAt.getTime());
        setItems(rows.map((r) => ({ id: r.id, title: r.title })));
        setLoading(false);
      },
      (err) => {
        console.error('[SessionMyAgendaPoints] onSnapshot:', err);
        if (!cancelled) {
          setItems([]);
          setLoading(false);
        }
      },
    );
    return () => {
      cancelled = true;
      unsub();
    };
  }, [entityId, roomName, userId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.textSub }}>
        <Loader size={14} strokeWidth={2} className="animate-spin" />
        Laden…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <p style={{ fontSize: 13, color: T.textMuted, margin: 0, lineHeight: 1.55 }}>
        Geen agenda punten van jou voor deze meeting. Ze verschijnen hier als je ze in de meeting of van tevoren hebt toegevoegd.
      </p>
    );
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {items.map((it) => (
        <li key={it.id} style={{ fontSize: 13, color: T.text, marginBottom: 6, lineHeight: 1.55 }}>
          {it.title}
        </li>
      ))}
    </ul>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Session Detail
// ─────────────────────────────────────────────────────────────────────────────
const SessionDetail: React.FC<{
  session: SessionRecording;
  userId: string;
  isHost: boolean;
  onBack: () => void;
}> = ({ session, userId, isHost, onBack }) => {
  const hasVideo = Boolean(session.downloadURL);
  const isAnalyzed = session.status === 'analyzed';
  const isPending = session.status === 'pending_analysis';
  const title = hasVideo ? 'Meeting Opname' : 'Meeting Samenvatting';

  // For "Meeting Samenvatting" — editable notes
  const [editMode, setEditMode] = useState(false);
  const [summaryText, setSummaryText] = useState(session.summary || '');

  const saveSummary = async () => {
    if (!session.reflectionId) return;
    await updateDoc(doc(db, 'meetingReflections', session.reflectionId), { summary: summaryText }).catch(() => {});
    setEditMode(false);
  };

  return (
    <div style={{ flex: 1, overflowY: 'auto' }}>
      {/* Back + title */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: T.textSub, padding: 0 }}>
          ← Terug
        </button>
        {hasVideo && (
          <a href={session.downloadURL} target="_blank" rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, color: T.textSub, textDecoration: 'none' }}>
            <DownloadIcon size={13} weight="regular" />Download
          </a>
        )}
      </div>

      {/* Date + status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 12, color: T.textSub }}>
          <CalendarIcon size={13} weight="regular" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />{fmtDateLong(session.createdAt)}
        </span>
        {isAnalyzed && (
          <span style={{ fontSize: 11, fontWeight: 700, color: T.accent, background: T.accentBg, borderRadius: 20, padding: '2px 9px', border: `1px solid ${T.accent}40` }}>
            Geanalyseerd
          </span>
        )}
        {isPending && (
          <span style={{ fontSize: 11, fontWeight: 600, color: T.textSub, background: T.bg, borderRadius: 20, padding: '2px 9px' }}>
            Wordt geanalyseerd…
          </span>
        )}
      </div>

      {/* Title */}
      <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: '0 0 4px' }}>{title}</h2>
      {hasVideo && (
        <div style={{ fontSize: 12.5, color: T.textSub, marginBottom: 14 }}>{formatDuration(session.durationSeconds)}</div>
      )}

      {/* Video player */}
      {hasVideo && (
        <div style={{ borderRadius: 14, overflow: 'hidden', background: '#000', marginBottom: 16, position: 'relative' }}>
          <video
            src={session.downloadURL}
            controls
            style={{ width: '100%', maxHeight: 320, display: 'block' }}
          />
        </div>
      )}

      {/* Meeting Samenvatting — AI notes met Bewerken/Opslaan/Wissen */}
      {!hasVideo && (
        <div style={{ marginBottom: 16 }}>
          {/* Action buttons */}
          {isHost && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' as const }}>
              {!editMode
                ? <button onClick={() => setEditMode(true)} style={{ ...S.headerBtn, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 5 }}><EditIcon size={13} weight="regular" /> Bewerken</button>
                : <>
                    <button onClick={saveSummary} style={{ ...S.headerBtn, fontSize: 12, background: T.accentBg, color: T.accent, borderColor: T.accent, display: 'inline-flex', alignItems: 'center', gap: 5 }}><SaveDiskIcon size={13} weight="regular" /> Opslaan</button>
                    <button onClick={() => setEditMode(false)} style={{ ...S.headerBtn, fontSize: 12, color: T.red, borderColor: T.red, display: 'inline-flex', alignItems: 'center', gap: 5 }}><DeleteIcon size={13} weight="regular" /> Wissen</button>
                  </>
              }
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.textSub, padding: '0 12px', background: T.bg, borderRadius: 20, border: `1px solid ${T.border}` }}>
                <ClipboardIcon size={13} weight="regular" /> Sessienotities
              </span>
            </div>
          )}
          {editMode ? (
            <textarea value={summaryText} onChange={e => setSummaryText(e.target.value)} rows={6}
              style={{ width: '100%', borderRadius: 10, border: `1px solid ${T.border}`, padding: '10px 12px', fontSize: 13, outline: 'none', resize: 'vertical', color: T.text, boxSizing: 'border-box' }} />
          ) : (
            <div style={{ minHeight: 80, fontSize: 13, color: summaryText ? T.text : T.textMuted, lineHeight: 1.6, display: 'flex', flexDirection: 'column', alignItems: summaryText ? 'flex-start' : 'center', justifyContent: summaryText ? 'flex-start' : 'center', padding: summaryText ? 0 : '24px 0' }}>
              {summaryText ? summaryText : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><AssistantIcon size={16} weight="regular" color={T.textSub} /></div>
                  <span>AI-analyse wordt gegenereerd...</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Accordions */}
      <div style={{ ...S.card, overflow: 'hidden' }}>
        {/* Gegenereerde samenvatting */}
        <AccordionRow icon={<AssistantIcon size={15} weight="regular" />} title="Gegenereerde samenvatting" defaultOpen={isAnalyzed || isPending}>
          {isPending ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.textSub }}>
              <Loader size={14} strokeWidth={2} className="animate-spin" />AI-analyse wordt gegenereerd…
            </div>
          ) : session.summary ? (
            <p style={{ fontSize: 13, color: T.text, lineHeight: 1.6, margin: 0 }}>{session.summary}</p>
          ) : (
            <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>Geen spraak herkend in de opname.</p>
          )}
          {isAnalyzed && session.bulletPoints && session.bulletPoints.length > 0 && (
            <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
              {session.bulletPoints.map((bp, i) => (
                <li key={i} style={{ fontSize: 12.5, color: T.textSub, marginBottom: 4 }}>{bp}</li>
              ))}
            </ul>
          )}
        </AccordionRow>

        {/* Jouw privé notities */}
        <AccordionRow icon={<EditIcon size={15} weight="regular" />} title="Jouw privé notities">
          <PrivateNotes recordingId={session.id} userId={userId} />
        </AccordionRow>

        {/* Mijn agenda punten */}
        <AccordionRow icon={<ClipboardIcon size={15} weight="regular" />} title="Mijn agenda punten">
          <SessionMyAgendaPoints entityId={session.entityId} roomName={session.roomName} userId={userId} />
        </AccordionRow>

        {/* Mijn persoonlijke analyse */}
        <AccordionRow icon={<ProfileIcon size={15} weight="regular" />} title="Mijn persoonlijke analyse" subtitle="Privé · nog niet ingevuld">
          <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>
            Nog niet ingevuld. Vul de vragen in na afloop van een meeting.
          </p>
        </AccordionRow>

        {/* Aanwezig */}
        <AccordionRow
          icon={<CommunityIcon size={15} weight="regular" />}
          title={`${(session.participants || []).length} aanwezig`}>
          {(session.participants || []).length === 0 ? (
            <p style={{ fontSize: 13, color: T.textMuted, margin: 0 }}>Geen aanwezigen opgeslagen.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {(session.participants || []).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {p.avatarUrl
                    ? <img src={p.avatarUrl} alt={p.displayName} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
                    : <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: T.accent }}>{(p.displayName || 'L')[0]}</div>
                  }
                  <span style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{p.displayName}</span>
                </div>
              ))}
              <a href="#" style={{ fontSize: 12, color: T.accent, marginTop: 4, display: 'inline-flex', alignItems: 'center', gap: 4 }}><MailIcon size={13} weight="regular" /> Contact host</a>
            </div>
          )}
        </AccordionRow>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Next Meeting Popup
// ─────────────────────────────────────────────────────────────────────────────
// Helper: get UTC offset in minutes for a given timezone at a specific date
function getTZOffsetMin(tz: string, at: Date): number {
  try {
    const toNum = (s: string) => { const [h, m] = s.split(':').map(Number); return h * 60 + m; };
    const tzTime  = toNum(at.toLocaleTimeString('en-GB', { timeZone: tz,    hour: '2-digit', minute: '2-digit', hour12: false }));
    const utcTime = toNum(at.toLocaleTimeString('en-GB', { timeZone: 'UTC', hour: '2-digit', minute: '2-digit', hour12: false }));
    let diff = tzTime - utcTime;
    if (diff >  720) diff -= 1440;
    if (diff < -720) diff += 1440;
    return diff;
  } catch { return 0; }
}

const REMINDER_DOC = (userId: string, entityId: string) =>
  doc(db, 'userMeetingReminders', `${userId}__${entityId}`);

const NextMeetingPopup: React.FC<{
  event: any;
  members: MemberWithMeta[];
  userId: string;
  entityId: string;
  userDisplayName: string;
  userPhotoURL?: string | null;
  isAdmin?: boolean;
  onClose: () => void;
  onPlanMeeting?: () => void;
  onJoinMeeting?: () => void;
}> = ({ event, members, userId, entityId, userDisplayName, userPhotoURL, isAdmin, onClose, onPlanMeeting, onJoinMeeting }) => {
  const hasEvent = !!event;
  const d = event?.eventDate instanceof Date ? event.eventDate : new Date(event?.eventDate || Date.now() + 86400000);
  const timeStr = event?.startTime || d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });

  // ─── State ────────────────────────────────────────────────────────────────
  const [now, setNow]                             = useState(() => new Date());
  const [countdown, setCountdown]                 = useState('');
  const [isAlwaysOnTime, setIsAlwaysOnTime]       = useState(false);
  const [showOnTimeOverlay, setShowOnTimeOverlay] = useState(false);
  const [reminderSettings, setReminderSettings]   = useState({ h24: true, h1: true, min10: false });
  const [autoJoin, setAutoJoin]                   = useState(false);
  const [saved, setSaved]                         = useState(false);
  const [notifPermission, setNotifPermission]     = useState<NotificationPermission>('default');
  const [loadingPrefs, setLoadingPrefs]           = useState(true);

  // ─── Load saved preferences from Firebase ─────────────────────────────────
  useEffect(() => {
    if (!userId || !entityId) { setLoadingPrefs(false); return; }
    getDoc(REMINDER_DOC(userId, entityId)).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setIsAlwaysOnTime(data.enabled ?? false);
        setReminderSettings(data.reminders ?? { h24: true, h1: true, min10: false });
        setAutoJoin(data.autoJoin ?? false);
      }
    }).catch(() => {}).finally(() => setLoadingPrefs(false));
    // Check current notification permission
    if (typeof Notification !== 'undefined') {
      setNotifPermission(Notification.permission);
    }
  }, [userId, entityId]);

  // ─── Timezones ────────────────────────────────────────────────────────────
  const userTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const hostTimezone: string = event?.hostTimezone || 'Europe/Amsterdam';
  const userCity  = userTimezone.split('/').pop()?.replace(/_/g, ' ') ?? userTimezone;
  const hostCity  = hostTimezone.split('/').pop()?.replace(/_/g, ' ') ?? hostTimezone;

  // ─── Live clocks + countdown (per minute) ────────────────────────────────
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      setNow(n);
      const diffMs = d.getTime() - n.getTime();
      if (diffMs <= 0) { setCountdown('Nu bezig'); return; }
      const totalMin = Math.floor(diffMs / 60000);
      const days  = Math.floor(totalMin / 1440);
      const hours = Math.floor((totalMin % 1440) / 60);
      const mins  = totalMin % 60;
      if (days  > 0) setCountdown(`Over ${days}d ${hours}u`);
      else if (hours > 0) setCountdown(`Over ${hours}u ${mins}m`);
      else setCountdown(`Over ${mins} min`);
    };
    tick();
    const t = setInterval(tick, 60_000);
    return () => clearInterval(t);
  }, [d]);

  // Live clock strings
  const userNow = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: userTimezone });
  const hostNow = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: hostTimezone });

  // Time difference in hours
  const tzDiffH = useMemo(() => {
    try {
      const diff = (getTZOffsetMin(userTimezone, now) - getTZOffsetMin(hostTimezone, now)) / 60;
      return diff;
    } catch { return 0; }
  }, [userTimezone, hostTimezone, now]);

  const diffLabel = tzDiffH === 0
    ? '0u'
    : `${tzDiffH > 0 ? '+' : ''}${Number.isInteger(tzDiffH) ? tzDiffH : tzDiffH.toFixed(1)}u`;

  const handleSaveOnTime = async () => {
    // 1. Save to Firebase
    if (userId && entityId) {
      await setDoc(REMINDER_DOC(userId, entityId), {
        enabled: true,
        reminders: reminderSettings,
        autoJoin,
        userTimezone,
        updatedAt: serverTimestamp(),
      }).catch(() => {});
    }
    // 2. Request browser notification permission
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      const perm = await Notification.requestPermission().catch(() => 'denied' as NotificationPermission);
      setNotifPermission(perm);
    }
    // 3. Confirm to user
    setIsAlwaysOnTime(true);
    setSaved(true);
    setShowOnTimeOverlay(false);
    setTimeout(() => setSaved(false), 5000);
  };

  const handleToggleOff = async () => {
    setIsAlwaysOnTime(false);
    if (userId && entityId) {
      await setDoc(REMINDER_DOC(userId, entityId), {
        enabled: false, reminders: reminderSettings, autoJoin, userTimezone, updatedAt: serverTimestamp(),
      }, { merge: true }).catch(() => {});
    }
  };

  return (
    <div style={{ width: 420, background: T.white, borderRadius: 18, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: `1px solid ${T.border}`, overflow: 'hidden', position: 'relative' }}>

      {/* ═══ TOP: Dual clock bar ═══════════════════════════════════════════════ */}
      <div style={{ background: T.bg, padding: '14px 18px 12px', display: 'flex', alignItems: 'center', gap: 0 }}>
        {/* Jouw klok */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: T.text, letterSpacing: -1, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{userNow}</div>
          <div style={{ fontSize: 10, fontWeight: 600, color: T.accent, marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Jouw tijd</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>{userCity}</div>
        </div>

        {/* Tijdverschil in het midden */}
        <div style={{ flexShrink: 0, width: 64, textAlign: 'center' }}>
          <div style={{
            fontSize: 13, fontWeight: 800, color: tzDiffH === 0 ? T.textMuted : T.accent,
            background: T.white, borderRadius: 20, padding: '4px 10px',
            border: `1px solid ${tzDiffH === 0 ? T.border : T.accent}`,
            display: 'inline-block', lineHeight: 1.2,
          }}>{diffLabel}</div>
          <div style={{ fontSize: 9, color: T.textMuted, marginTop: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>verschil</div>
        </div>

        {/* Host klok */}
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 26, fontWeight: 800, color: T.text, letterSpacing: -1, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{hostNow}</div>
          <div style={{ fontSize: 10, fontWeight: 600, color: T.textMuted, marginTop: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>Host tijd</div>
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>{hostCity}</div>
        </div>
      </div>

      {/* ═══ BODY: meeting info + leden ════════════════════════════════════════ */}
      <div style={{ padding: '14px 18px 16px' }}>

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <span style={{ fontSize: 10.5, fontWeight: 800, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>Volgende meeting</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
        </div>

        {userId && entityId && (
          <PreMeetingAgendaPointsPanel
            entityId={entityId}
            userId={userId}
            userName={userDisplayName}
            userPhotoURL={userPhotoURL}
            members={members.map((m) => ({ id: m.id, name: m.name, photoURL: m.photoURL }))}
            canDeleteOthers={!!isAdmin}
            colors={{
              bg: T.bg,
              white: T.white,
              text: T.text,
              textMuted: T.textMuted,
              textSub: T.textSub,
              border: T.border,
              accent: T.accent,
              accentLight: T.accentLight,
              accentBg: T.accentBg,
            }}
          />
        )}

        {/* No event state */}
        {!hasEvent && (
          <div style={{ background: T.bg, borderRadius: 10, padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 2 }}>Geen meeting gepland</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>Plan een volgende sessie via de Agenda</div>
            </div>
            {isAdmin && onPlanMeeting && (
              <button onClick={onPlanMeeting}
                style={{ fontSize: 11.5, fontWeight: 700, color: T.accent, background: T.accentLight, border: `1px solid ${T.accent}`, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Inplannen →
              </button>
            )}
          </div>
        )}

        {/* Event title + date */}
        {hasEvent && (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, marginBottom: 2 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event?.title || 'Bijeenkomst'}</div>
              {countdown && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: T.accent, background: T.accentLight, borderRadius: 20, padding: '2px 8px', flexShrink: 0 }}>
                  <TimeIcon size={10} weight="fill" />{countdown}
                </div>
              )}
            </div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
              {d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })} · {timeStr}
            </div>
          </>
        )}

        {/* Members */}
        {hasEvent && members.length > 0 && (
          <>
            <div style={{ fontSize: 10, fontWeight: 800, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 }}>Leden — Lokale tijd</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {members.map(m => {
                const isSelf = m.id === userId;
                const initials = m.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
                const memberNow = now.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', ...(m.timezone ? {} : {}) });
                return (
                  <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 10, background: isSelf ? T.accentLight : T.bg, border: `1px solid ${isSelf ? T.accent : T.border}` }}>
                    {m.photoURL
                      ? <img src={m.photoURL} alt={m.name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      : <div style={{ width: 28, height: 28, borderRadius: '50%', background: T.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: T.accent, flexShrink: 0 }}>{initials}</div>
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{m.name}{isSelf ? ' (jij)' : ''}</div>
                    </div>
                    <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: T.textSub }}>{timeStr}</div>
                      <div>{m.timezone || getGmtOffset()}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* ─── "Altijd op tijd" toggle ─── */}
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.borderLight}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={() => { if (isAlwaysOnTime) { void handleToggleOff(); } else { setShowOnTimeOverlay(true); } }}
              style={{ width: 34, height: 20, borderRadius: 10, background: isAlwaysOnTime ? T.accent : T.border, border: 'none', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0, padding: 0 }}
              aria-label="Altijd op tijd">
              <span style={{ position: 'absolute', top: 3, width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left 0.2s', left: isAlwaysOnTime ? 17 : 3, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </button>
            <span style={{ fontSize: 12, color: T.textSub, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => { setIsAlwaysOnTime(true); setShowOnTimeOverlay(true); }}>
              Altijd op tijd
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {saved && <span style={{ fontSize: 11, color: T.accent, fontWeight: 700 }}>✓ Ingesteld</span>}
            {isAlwaysOnTime && !saved && (
              <button onClick={() => setShowOnTimeOverlay(true)}
                style={{ fontSize: 11, color: T.accent, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                Bewerken
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ═══ OVERLAY: "Altijd op tijd" — gecentreerde fullscreen modal ══════ */}
      {showOnTimeOverlay && (
        <div
          onClick={() => setShowOnTimeOverlay(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{ width: '100%', maxWidth: 480, background: T.white, borderRadius: 22, boxShadow: '0 16px 56px rgba(0,0,0,0.22)', border: `1px solid ${T.border}`, overflow: 'hidden' }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '22px 24px 18px' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Altijd op tijd</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>Stel herinneringen in zodat je nooit een meeting mist</div>
              </div>
              <button onClick={() => setShowOnTimeOverlay(false)}
                style={{ width: 32, height: 32, borderRadius: '50%', background: T.bg, border: 'none', cursor: 'pointer', color: T.textSub, fontSize: 18, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>×</button>
            </div>

            <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Timezone info */}
              <div style={{ background: T.bg, borderRadius: 14, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <ClockIcon size={20} weight="regular" color={T.accent} />
                <div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>Gedetecteerde tijdzone</div>
                  <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text, marginTop: 1 }}>{userTimezone}</div>
                </div>
              </div>

              {/* Notification permission status */}
              {notifPermission === 'denied' && (
                <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#92400E' }}>
                  <strong>Notificaties geblokkeerd</strong> — Sta notificaties toe in je browser-instellingen om herinneringen te ontvangen.
                </div>
              )}
              {notifPermission === 'granted' && (
                <div style={{ background: T.accentLight, border: `1px solid ${T.accent}`, borderRadius: 10, padding: '10px 14px', fontSize: 12, color: T.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <CheckIcon size={14} weight="bold" color={T.accent} />
                  <span>Browser-notificaties zijn <strong>ingeschakeld</strong></span>
                </div>
              )}

              {/* Reminders */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9, marginBottom: 12 }}>Herinner mij</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {([
                    { key: 'h24',   label: '24 uur van tevoren',      sub: 'Een dag eerder een reminder' },
                    { key: 'h1',    label: '1 uur van tevoren',       sub: 'Ruim op tijd om je voor te bereiden' },
                    { key: 'min10', label: '10 minuten van tevoren',  sub: 'Laatste herinnering vlak voor de meeting' },
                  ] as const).map(({ key, label, sub }) => (
                    <label key={key}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', background: reminderSettings[key] ? T.accentLight : T.bg, border: `1px solid ${reminderSettings[key] ? T.accent : T.border}`, transition: 'all 0.15s' }}>
                      <input type="checkbox" checked={reminderSettings[key]}
                        onChange={e => setReminderSettings(s => ({ ...s, [key]: e.target.checked }))}
                        style={{ accentColor: T.accent, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: T.text }}>{label}</div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>{sub}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Auto join */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.9, marginBottom: 12 }}>Automatisch</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12, cursor: 'pointer', background: autoJoin ? T.accentLight : T.bg, border: `1px solid ${autoJoin ? T.accent : T.border}`, transition: 'all 0.15s' }}>
                  <input type="checkbox" checked={autoJoin} onChange={e => setAutoJoin(e.target.checked)}
                    style={{ accentColor: T.accent, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: T.text }}>Meeting automatisch openen</div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 1 }}>Browser opent de meeting op het ingestelde tijdstip</div>
                  </div>
                </label>
              </div>

              {/* Save */}
              <button onClick={handleSaveOnTime}
                style={{ width: '100%', padding: '13px 0', background: T.accent, color: '#fff', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, letterSpacing: 0.2 }}>
                Opslaan — niet meer nadenken
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN: MastermindCommunityHub
// ─────────────────────────────────────────────────────────────────────────────
const MastermindCommunityHub: React.FC = () => {
  const [user] = useAuthState(auth);
  const { entities, activeEntityId, setActiveEntityId } = useEntityContext(user?.uid);

  // Als de actieve entiteit geen mastermind/community is, automatisch switchen naar de eerste
  const mastermindEntities = useMemo(
    () => entities.filter(e => e.type === 'mastermind' || e.type === 'community'),
    [entities]
  );

  useEffect(() => {
    if (mastermindEntities.length === 0) return;
    const currentIsMastermind = mastermindEntities.some(e => e.id === activeEntityId);
    if (!currentIsMastermind) {
      setActiveEntityId(mastermindEntities[0].id);
    }
  }, [mastermindEntities, activeEntityId, setActiveEntityId]);

  const activeEntity = mastermindEntities.find(e => e.id === activeEntityId);

  // ── Core state ──────────────────────────────────────────────────────────────
  const [club, setClub] = useState<NetworkClub | null>(null);
  const [members, setMembers] = useState<MemberWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [clubEvents, setClubEvents] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'onsverhaal' | 'sessies' | 'groei' | 'agenda' | 'instellingen'>('onsverhaal');
  const [clubLandingView, setClubLandingView] = useState<'overview' | 'story' | 'website'>('overview');
  const [websiteInput, setWebsiteInput] = useState('');
  const [websiteSaving, setWebsiteSaving] = useState(false);
  const [websiteSaved, setWebsiteSaved] = useState(false);
  const [websiteError, setWebsiteError] = useState('');

  // ── Sessies tab state ───────────────────────────────────────────────────────
  const [sessions, setSessions] = useState<SessionRecording[]>([]);
  const [selectedSession, setSelectedSession] = useState<SessionRecording | null>(null);
  const [sessionsYear, setSessionsYear] = useState(new Date().getFullYear());
  const [sessionsLoading, setSessionsLoading] = useState(false);

  // ── Header popup state ──────────────────────────────────────────────────────
  const [showNextMeetingPopup, setShowNextMeetingPopup] = useState(false);
  const [nextMeetingAnchor, setNextMeetingAnchor] = useState<{ top: number; right: number } | null>(null);
  const nextMeetingBtnRef = useRef<HTMLDivElement>(null);
  const [showLedenPanel, setShowLedenPanel] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // ── Meeting state ───────────────────────────────────────────────────────────
  const [showMeeting, setShowMeeting] = useState(false);
  const [meetingRoom, setMeetingRoom] = useState<string | null>(null);
  const [activeMeeting, setActiveMeeting] = useState<{ roomName: string; startedByName: string; startedAt: Date | null } | null>(null);
  const [showPostSession, setShowPostSession] = useState(false);
  const [completedMeetingId, setCompletedMeetingId] = useState<string | null>(null);
  const [showPreMeeting, setShowPreMeeting] = useState(false);
  const [preMeetingOpenTab, setPreMeetingOpenTab] = useState<'sessie' | undefined>(undefined);
  /** Countdown draait (Firestore) — ook zichtbaar voor leden buiten de meeting */
  const [countdownRunningRemote, setCountdownRunningRemote] = useState(false);
  const [showCountdownViewer, setShowCountdownViewer] = useState(false);
  const [growthCardHover, setGrowthCardHover] = useState<string | null>(null);

  const scrollToGrowthSection = useCallback((sectionId: 'mm-growth-prep' | 'mm-growth-goal' | 'mm-growth-commitment' | 'mm-growth-health') => {
    requestAnimationFrame(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // ── Feature toggles (synced met meetingSettings/{entityId}) ─────────────────
  const [featureToggles, setFeatureToggles] = useState({
    docView:          false,
    linkOpener:       true,
    guestSpotlight:   true,
    commitmentReview: true,
    slideshow:        true,
    taskReminder:     false,
    autoRecord:       false,
  });

  // Laad feature-toggles zodra de entity bekend is
  useEffect(() => {
    if (!activeEntity?.id) return;
    const unsub = onSnapshot(doc(db, 'meetingSettings', activeEntity.id), snap => {
      if (!snap.exists()) return;
      const d = snap.data() as any;
      setFeatureToggles(prev => ({
        docView:          typeof d.docViewEnabled          === 'boolean' ? d.docViewEnabled          : prev.docView,
        linkOpener:       typeof d.linkOpenerEnabled       === 'boolean' ? d.linkOpenerEnabled       : prev.linkOpener,
        guestSpotlight:   typeof d.guestSpotlightEnabled   === 'boolean' ? d.guestSpotlightEnabled   : prev.guestSpotlight,
        commitmentReview: typeof d.commitmentReviewEnabled === 'boolean' ? d.commitmentReviewEnabled : prev.commitmentReview,
        slideshow:        typeof d.slideshowEnabled        === 'boolean' ? d.slideshowEnabled        : prev.slideshow,
        taskReminder:     typeof d.taskReminderEnabled     === 'boolean' ? d.taskReminderEnabled     : prev.taskReminder,
        autoRecord:       typeof d.autoRecordEnabled       === 'boolean' ? d.autoRecordEnabled       : prev.autoRecord,
      }));
      setCountdownRunningRemote(!!d?.countdownRunning);
    }, () => {});
    return () => unsub();
  }, [activeEntity?.id]);

  useEffect(() => {
    if (!countdownRunningRemote) setShowCountdownViewer(false);
  }, [countdownRunningRemote]);

  // ── Cover state ─────────────────────────────────────────────────────────────
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [coverPosition, setCoverPosition] = useState({ x: 50, y: 50 });
  const coverPositionRef = useRef({ x: 50, y: 50 });
  const [isCoverDragging, setIsCoverDragging] = useState(false);
  const [isCoverEditMode, setIsCoverEditMode] = useState(false);
  const [savingCover, setSavingCover] = useState(false);
  const [savingLogo, setSavingLogo] = useState(false);
  const coverInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const coverFrameRef = useRef<HTMLDivElement>(null);

  // ── Access ──────────────────────────────────────────────────────────────────
  const [accessOk, setAccessOk] = useState(false);
  const [accessLoading, setAccessLoading] = useState(true);

  const isOwner = activeEntity?.role === 'owner';
  const isOrganizerRole = activeEntity?.role === 'organizer';
  const isBoard = activeEntity?.role === 'board';

  const hubCaps = useMemo(
    () => resolveHubCapabilities(activeEntity?.role, activeEntity?.capabilities ?? undefined),
    [activeEntity?.role, activeEntity?.capabilities]
  );

  const isMeetingHost = isOwner || isBoard || (isOrganizerRole && hubCaps.canStartMeeting);
  const canInviteMembersHub = isOwner || isBoard || (isOrganizerRole && hubCaps.canInviteMembers);
  const canManageOverlaysHub = isOwner || isBoard || (isOrganizerRole && hubCaps.canManageOverlays);
  const canEditBrandingHub = isOwner || isBoard || (isOrganizerRole && hubCaps.canEditBranding);
  const canManageAgendaHub = isOwner || isBoard || (isOrganizerRole && hubCaps.canManageAgenda);
  const canManageGrowthHub = isOwner || isBoard || (isOrganizerRole && hubCaps.canManageGrowth);
  const canManageSessionsHub = isOwner || isBoard || (isOrganizerRole && hubCaps.canManageSessions);
  const isNextMeetingAdmin = isOwner || isBoard || (isOrganizerRole && (hubCaps.canManageAgenda || hubCaps.canInviteMembers));
  // canManageGuests: opgeslagen op `entityMembers.capabilities`; Jitsi guest-UI volgt in volgende iteratie.

  const preMeetingSessionSetupOnly = Boolean(preMeetingOpenTab === 'sessie' && canManageSessionsHub && !isMeetingHost);

  const handleFeatureToggle = useCallback(async (key: keyof typeof featureToggles, val: boolean) => {
    if (!canManageOverlaysHub) return;
    setFeatureToggles(prev => ({ ...prev, [key]: val }));
    if (!activeEntity?.id) return;
    await setDoc(doc(db, 'meetingSettings', activeEntity.id), {
      [`${key}Enabled`]: val,
    }, { merge: true }).catch(() => {});
    if (key === 'taskReminder') {
      await setDoc(doc(db, 'entitySettings', activeEntity.id), {
        taskReminderEnabled: val,
      }, { merge: true }).catch(() => {});
    }
  }, [activeEntity?.id, canManageOverlaysHub]);

  // ── ESC key ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
    document.addEventListener('keydown', fn);
    return () => document.removeEventListener('keydown', fn);
  }, []);

  // ── Access check ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeEntity) { setAccessOk(false); setAccessLoading(false); return; }
    const r = activeEntity.role;
    setAccessOk(r === 'owner' || r === 'organizer' || r === 'board' || r === 'member');
    setAccessLoading(false);
  }, [activeEntity]);

  // ── Tab reset ────────────────────────────────────────────────────────────────
  useEffect(() => { setActiveTab('sessies'); }, [activeEntity?.id]);

  // ── Cover sync ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!club) return;
    setCoverImageUrl((club as any).coverImageUrl || '');
    const pos = (club as any).coverPosition;
    if (pos?.x != null) { setCoverPosition({ x: pos.x, y: pos.y }); coverPositionRef.current = { x: pos.x, y: pos.y }; }
  }, [club?.id, (club as any)?.coverImageUrl]); // eslint-disable-line

  const updateCoverFromEvent = useCallback((cx: number, cy: number) => {
    const f = coverFrameRef.current; if (!f) return;
    const r = f.getBoundingClientRect();
    const next = { x: Math.max(0, Math.min(100, ((cx - r.left) / r.width) * 100)), y: Math.max(0, Math.min(100, ((cy - r.top) / r.height) * 100)) };
    setCoverPosition(next); coverPositionRef.current = next;
  }, []);

  useEffect(() => {
    if (!isCoverDragging) return;
    const onMove = (e: MouseEvent) => updateCoverFromEvent(e.clientX, e.clientY);
    const onUp = async () => {
      setIsCoverDragging(false); setIsCoverEditMode(false);
      if (club) await updateDoc(doc(db, 'networkClubs', club.id), { coverPosition: coverPositionRef.current }).catch(() => {});
    };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isCoverDragging, updateCoverFromEvent, club]);

  // ── Meeting reminder checker ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.uid || !activeEntity?.id) return;
    const firedRef = new Set<string>();

    const checkReminders = async () => {
      try {
        const snap = await getDoc(REMINDER_DOC(user.uid, activeEntity.id));
        if (!snap.exists()) return;
        const prefs = snap.data();
        if (!prefs.enabled) return;

        const evt = clubEvents.find((e: any) => e.eventDate >= new Date());
        if (!evt) return;

        const diffMin = (evt.eventDate.getTime() - Date.now()) / 60000;
        if (diffMin < 0) return;

        const windows = [
          { key: '24h',   enabled: prefs.reminders?.h24,   low: 1438, high: 1442, label: '24 uur' },
          { key: '1h',    enabled: prefs.reminders?.h1,    low: 58,   high: 62,   label: '1 uur'  },
          { key: '10min', enabled: prefs.reminders?.min10, low: 8,    high: 12,   label: '10 minuten' },
        ];

        for (const w of windows) {
          if (w.enabled && diffMin >= w.low && diffMin <= w.high) {
            const fireKey = `${String(evt.id ?? evt.eventDate.getTime())}-${w.key}`;
            if (!firedRef.has(fireKey)) {
              firedRef.add(fireKey);
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                new Notification('SuperKonnected — Meeting herinnering', {
                  body: `"${evt.title || 'Meeting'}" begint over ${w.label}`,
                  icon: '/logo192.png',
                  tag: fireKey,
                });
              }
            }
          }
        }

        // Auto-join: open meeting if within 2 min of start
        if (prefs.autoJoin && diffMin >= -1 && diffMin <= 1 && !showMeeting) {
          const autoKey = `autojoin-${String(evt.id ?? evt.eventDate.getTime())}`;
          if (!firedRef.has(autoKey)) {
            firedRef.add(autoKey);
            if (evt.roomName) { setMeetingRoom(evt.roomName); setShowMeeting(true); }
          }
        }
      } catch { /* silent */ }
    };

    checkReminders();
    const t = setInterval(checkReminders, 60_000);
    return () => clearInterval(t);
  }, [user?.uid, activeEntity?.id, clubEvents, showMeeting]);

  // ── Data loading ─────────────────────────────────────────────────────────────
  const loadData = useCallback(async (entityId: string) => {
    setLoading(true);
    try {
      const clubData = await networkClubService.getNetworkClubByEntityId(entityId);
      if (!clubData) { setLoading(false); return; }
      const entitySnap = await getDoc(doc(db, 'entities', entityId));
      const eData: any = entitySnap.exists() ? entitySnap.data() : {};
      setClub({
        ...(clubData as any),
        coverImageUrl: (clubData as any).coverImageUrl || eData.coverImageUrl || '',
        coverPosition: (clubData as any).coverPosition || eData.coverPosition || { x: 50, y: 50 },
        logoUrl: (clubData as any).logoUrl || eData.logoUrl || '',
      } as any);

      // Load members
      const memSnap = await getDocs(query(collection(db, 'entityMembers'), where('entityId', '==', entityId)));
      const mems: MemberWithMeta[] = [];
      await Promise.all(memSnap.docs.map(async md => {
        const mData = md.data();
        const [uSnap, pSnap] = await Promise.all([getDoc(doc(db, 'users', mData.userId)), getDoc(doc(db, 'userProfiles', mData.userId))]);
        const u: any = uSnap.exists() ? uSnap.data() : {};
        const p: any = pSnap.exists() ? pSnap.data() : {};
        const country = p.country || u.country || u.onboardingData?.country || '';
        mems.push({
          id: mData.userId, clubId: clubData.id, entityId,
          name: (u.displayName || p.displayName || mData.displayName || (u.email || '').split('@')[0] || 'Lid').trim(),
          email: u.email || p.email || mData.email || '',
          photoURL: u.photoURL || p.photoURL || mData.photoURL || undefined,
          role: mData.role || 'member', joinedAt: mData.createdAt?.toDate() || new Date(), status: 'active',
          country, timezone: getGmtOffset(),
          capabilities: mData.capabilities && typeof mData.capabilities === 'object' ? mData.capabilities : (mData.capabilities === null ? null : undefined),
        });
      }));
      mems.sort((a, b) => (ROLE_SORT[a.role] ?? 9) - (ROLE_SORT[b.role] ?? 9) || a.name.localeCompare(b.name, 'nl'));
      setMembers(mems);

      // Load events
      const evSnap = await getDocs(query(collection(db, 'calendarEvents'), where('entityId', '==', entityId))).catch(async () =>
        getDocs(query(collection(db, 'clubEvents'), where('entityId', '==', entityId)))
      );
      const evts = evSnap.docs.map(d => {
        const data: any = d.data();
        return { id: d.id, ...data, eventDate: data.eventDate?.toDate?.() || new Date(data.date || Date.now()) };
      }).filter((ev: any) => ev.eventDate instanceof Date && !isNaN(ev.eventDate.getTime()))
        .sort((a: any, b: any) => a.eventDate.getTime() - b.eventDate.getTime());
      setClubEvents(evts);
    } catch (err) { console.error('[MastermindHub] loadData:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    setActiveTab('onsverhaal');
  }, [activeEntity?.id]);

  useEffect(() => {
    setWebsiteInput(((club?.website as string) || ''));
    setWebsiteError('');
  }, [club?.id, club?.website]);

  const clubWebsiteUrl = useMemo(
    () => normalizeNetworkClubWebsiteUrl(club?.website || ''),
    [club?.website]
  );

  useEffect(() => {
    if (!normalizeNetworkClubWebsiteUrl(club?.website || '')) {
      if (clubLandingView === 'website') setClubLandingView('overview');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [club?.id, club?.website]);

  const saveClubWebsite = useCallback(async () => {
    if (!club || !activeEntity) return;
    setWebsiteError('');
    const normalized = normalizeNetworkClubWebsiteUrl(websiteInput);
    if (websiteInput.trim() && !normalized) {
      setWebsiteError('Voer een geldige URL in (bijv. https://jouwgroep.nl).');
      return;
    }
    setWebsiteSaving(true);
    try {
      await updateDoc(
        doc(db, 'networkClubs', club.id),
        normalized ? { website: normalized } : { website: deleteField() }
      );
      setWebsiteSaved(true);
      setTimeout(() => setWebsiteSaved(false), 2500);
      await loadData(activeEntity.id);
    } catch (e) {
      console.error('[MastermindHub] website save:', e);
      setWebsiteError('Opslaan mislukt. Controleer je rechten of probeer opnieuw.');
    } finally {
      setWebsiteSaving(false);
    }
  }, [club, activeEntity, websiteInput, loadData]);

  // ── Sessions loader ──────────────────────────────────────────────────────────
  const loadSessions = useCallback(async (entityId: string, year: number) => {
    setSessionsLoading(true);
    try {
      // Load recordings
      const recSnap = await getDocs(query(collection(db, 'meetingRecordings'), where('entityId', '==', entityId)));
      const recs = recSnap.docs.map(d => {
        const data: any = d.data();
        return { id: d.id, roomName: data.roomName || '', entityId, downloadURL: data.downloadURL || '', durationSeconds: data.durationSeconds || 0, participants: data.participants || [], createdAt: data.createdAt?.toDate?.() || new Date() };
      });

      // Load reflections (AI analysis) keyed by meetingId
      const refSnap = await getDocs(query(collection(db, 'meetingReflections'), where('entityId', '==', entityId)));
      const reflMap = new Map<string, any>();
      refSnap.docs.forEach(d => { const data: any = d.data(); reflMap.set(data.meetingId || data.roomName, { id: d.id, ...data }); });

      // Merge: recordings + reflections that have no recording
      const allIds = new Set<string>();
      const combined: SessionRecording[] = [];

      recs.forEach(rec => {
        allIds.add(rec.roomName);
        const refl = reflMap.get(rec.roomName);
        combined.push({
          ...rec,
          summary: refl?.summary || '',
          bulletPoints: refl?.bulletPoints || [],
          status: refl?.status || (rec.downloadURL ? 'recorded' : 'analyzed'),
          actiepunten: refl?.actiepunten || [],
          beslissingen: refl?.beslissingen || [],
          leerpunten: refl?.leerpunten || [],
          aiAnalysis: refl?.aiAnalysis,
          reflectionId: refl?.id,
        });
      });

      // Add stand-alone reflections (no recording)
      refSnap.docs.forEach(d => {
        const data: any = d.data();
        const meetingId = data.meetingId || data.roomName;
        if (!allIds.has(meetingId)) {
          combined.push({
            id: d.id, roomName: meetingId, entityId,
            downloadURL: undefined, durationSeconds: 0, participants: data.participants || [],
            createdAt: data.createdAt?.toDate?.() || new Date(),
            summary: data.summary || '', bulletPoints: data.bulletPoints || [],
            status: data.status || 'analyzed', actiepunten: data.actiepunten || [],
            beslissingen: data.beslissingen || [], leerpunten: data.leerpunten || [],
            aiAnalysis: data.aiAnalysis, reflectionId: d.id,
          });
        }
      });

      combined.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      setSessions(combined);
    } catch (err) { console.error('[MastermindHub] loadSessions:', err); }
    finally { setSessionsLoading(false); }
  }, []);

  // ── Main effects ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeEntity || !accessOk) return;
    loadData(activeEntity.id);
  }, [activeEntity?.id, accessOk, loadData]); // eslint-disable-line

  useEffect(() => {
    if (!activeEntity || !accessOk) return;
    loadSessions(activeEntity.id, sessionsYear);
  }, [activeEntity?.id, accessOk, sessionsYear, loadSessions]); // eslint-disable-line

  // Active meeting listener
  useEffect(() => {
    if (!activeEntity) return;
    const unsub = onSnapshot(doc(db, 'activeMeetings', activeEntity.id), snap => {
      if (snap.exists()) {
        const d = snap.data();
        setActiveMeeting({ roomName: d.roomName, startedByName: d.startedByName, startedAt: d.startedAt?.toDate?.() ?? null });
      } else { setActiveMeeting(null); }
    }, () => setActiveMeeting(null));
    return () => unsub();
  }, [activeEntity?.id]); // eslint-disable-line

  // Auto-join event
  useEffect(() => {
    const fn = (e: Event) => {
      const { entityId: eid, roomName } = (e as CustomEvent).detail;
      if (activeEntity?.id === eid) { setMeetingRoom(roomName); setShowMeeting(true); }
    };
    window.addEventListener('mastermindAutoJoin', fn);
    return () => window.removeEventListener('mastermindAutoJoin', fn);
  }, [activeEntity]);

  // ── Cover upload ─────────────────────────────────────────────────────────────
  const handleCoverUpload = async (file: File) => {
    if (!activeEntity || !club) return;
    setSavingCover(true);
    try {
      const compressed = await compressForCover(file);
      const fr = storageRef(storage, `groupHeaders/${activeEntity.id}/${Date.now()}-${compressed.name}`);
      await uploadBytes(fr, compressed);
      const url = await getDownloadURL(fr);
      setCoverImageUrl(url);
      await updateDoc(doc(db, 'networkClubs', club.id), { coverImageUrl: url, coverPosition: coverPositionRef.current });
    } finally { setSavingCover(false); }
  };

  const handleLogoUpload = async (file: File) => {
    if (!activeEntity || !club) return;
    if (!file.type.startsWith('image/')) {
      alert('Kies een afbeeldingsbestand.');
      return;
    }
    setSavingLogo(true);
    try {
      const compressed = await compressForCover(file);
      const fr = storageRef(storage, `groupHeaders/${activeEntity.id}/logo_${Date.now()}-${compressed.name}`);
      await uploadBytes(fr, compressed);
      const url = await getDownloadURL(fr);
      await updateDoc(doc(db, 'networkClubs', club.id), { logoUrl: url });
      setClub((prev) => (prev ? { ...prev, logoUrl: url } : prev));
    } catch (err) {
      console.error('[MastermindHub] logo upload:', err);
      alert('Logo uploaden mislukt. Probeer opnieuw.');
    } finally {
      setSavingLogo(false);
    }
  };

  // ── Meeting ──────────────────────────────────────────────────────────────────
  const startMeeting = useCallback(async () => {
    if (!activeEntity || !user) return;
    const room = `mastermind-${activeEntity.id}-${Date.now()}`;
    setMeetingRoom(room); setShowMeeting(true);
    await setDoc(doc(db, 'activeMeetings', activeEntity.id), {
      roomName: room, entityId: activeEntity.id,
      startedBy: user.uid, startedByName: user.displayName || user.email || 'Organisator',
      startedAt: serverTimestamp(),
    });
  }, [activeEntity, user]);

  const leaveMeeting = useCallback(async () => {
    if (document.fullscreenElement) await document.exitFullscreen().catch(() => {});
    const room = meetingRoom;
    setShowMeeting(false); setMeetingRoom(null);
    if (room) { setCompletedMeetingId(room); setShowPostSession(true); }
    if (activeEntity && isMeetingHost) await deleteDoc(doc(db, 'activeMeetings', activeEntity.id)).catch(() => {});
  }, [meetingRoom, activeEntity, isMeetingHost]);

  const requestFullscreen = useCallback(() => {
    const el = document.getElementById('mm-meeting-container');
    if (el && !document.fullscreenElement) el.requestFullscreen().catch(() => {});
    else if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }, []);

  /** Jitsi staat los van de actieve tab: na openen (kort wachten op mount) fullscreen proberen. */
  useEffect(() => {
    if (!showMeeting || !meetingRoom) return;
    const t = window.setTimeout(() => {
      const el = document.getElementById('mm-meeting-container');
      if (el && !document.fullscreenElement) el.requestFullscreen().catch(() => {});
    }, 750);
    return () => window.clearTimeout(t);
  }, [showMeeting, meetingRoom]);

  // ── Derived ──────────────────────────────────────────────────────────────────
  const memberCount = members.length;
  const nextEvent = clubEvents.find(ev => ev.eventDate >= new Date());
  const rolLabel = isOwner ? 'Eigenaar' : isOrganizerRole ? 'Beheerder' : isBoard ? 'Bestuur' : 'Lid';
  const typeLabel = club?.type === 'community' ? 'Community' : 'Mastermind';

  // Sessions grouped by month for the given year
  const sessionsByMonth = useMemo(() => {
    const map: Record<string, SessionRecording[]> = {};
    sessions.filter(s => s.createdAt.getFullYear() === sessionsYear).forEach(s => {
      const key = s.createdAt.toLocaleDateString('nl-NL', { month: 'short' }).toUpperCase().slice(0, 3);
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    return map;
  }, [sessions, sessionsYear]);

  const MONTHS = ['JAN', 'FEB', 'MRT', 'APR', 'MEI', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEC'];

  // ── Early returns ─────────────────────────────────────────────────────────────
  if (accessLoading) return <div className="flex items-center justify-center h-96"><Loader size={26} strokeWidth={1.5} className="animate-spin text-orange-500" /></div>;

  if (!activeEntity) return (
    <div className="flex flex-col items-center justify-center h-96 gap-4">
      <div className="text-gray-400 text-sm">Selecteer een mastermind via de groepen-kiezer</div>
      <EntitySwitcher />
    </div>
  );

  if (!accessOk) return (
    <div className="flex items-center justify-center h-96">
      <div className="text-gray-400 text-sm">Geen toegang. Alleen leden kunnen deze pagina openen.</div>
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-96"><Loader size={26} strokeWidth={1.5} className="animate-spin text-orange-500" /></div>;

  if (!club) return (
    <div className="flex flex-col items-center justify-center h-96 gap-4">
      <div className="text-gray-400 text-sm">Geen mastermind gevonden</div>
      <EntitySwitcher />
    </div>
  );

  const logoInitial = club.name.charAt(0).toUpperCase();

  // ── KvK verificatie status ────────────────────────────────────────────────────
  const isKvkVerified = !!club?.isKvkValidated;
  const hasKvkNumber = !!(club?.kvkNumber);

  // ── RENDER ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: T.bg, minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>

      {/* KvK verificatiebanner — zichtbaar zolang niet geverifieerd */}
      {!isKvkVerified && (
        <div className="sticky top-0 z-50 flex items-center gap-3 px-4 py-2.5 bg-amber-50 border-b border-amber-200">
          <span className="text-amber-500 text-lg shrink-0">🔐</span>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-semibold text-amber-800">
              {hasKvkNumber
                ? 'KvK-verificatie in behandeling — sommige functies zijn nog beperkt'
                : 'KvK-nummer nog niet ingevuld — verifieer je bedrijf om alle functies te activeren'}
            </p>
            <p className="text-[11px] text-amber-600/80 leading-snug">
              SuperKonnected vereist KvK-verificatie ter bescherming van alle leden.
              {hasKvkNumber ? ' Je aanvraag wordt zo snel mogelijk beoordeeld.' : ' Ga naar Instellingen → Bedrijfsprofiel om je KvK-nummer in te vullen.'}
            </p>
          </div>
          {!hasKvkNumber && (isOwner || isBoard || isOrganizerRole) && (
            <button
              type="button"
              onClick={() => setActiveTab('instellingen')}
              className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-500 text-white text-[11px] font-semibold hover:bg-amber-600 transition-colors whitespace-nowrap"
            >
              Naar instellingen
            </button>
          )}
        </div>
      )}

      {/* Hidden file inputs */}
      <input ref={coverInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleCoverUpload(f); if (coverInputRef.current) coverInputRef.current.value = ''; }} />
      <input
        ref={logoInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void handleLogoUpload(f);
          if (logoInputRef.current) logoInputRef.current.value = '';
        }}
      />

      {/* ── HEADER ──────────────────────────────────────────────────────────── */}
      <div style={{ borderRadius: '0 0 0 0', overflow: 'hidden', boxShadow: T.shadow }}>
        {/* Cover */}
        <div
          ref={coverFrameRef}
          style={{ height: 168, position: 'relative', background: '#2A2A2A', cursor: canEditBrandingHub && isCoverEditMode ? 'move' : 'default', overflow: 'hidden' }}
          onMouseDown={e => {
            if (!canEditBrandingHub || !coverImageUrl || !isCoverEditMode) return;
            setIsCoverDragging(true); updateCoverFromEvent(e.clientX, e.clientY);
          }}
        >
          {coverImageUrl
            ? <img src={coverImageUrl} alt="" draggable={false} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${coverPosition.x}% ${coverPosition.y}%`, userSelect: 'none', pointerEvents: 'none' }} />
            : <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg,#1C2A38 0%,#2A3A48 100%)' }} />
          }
          {/* Subtle overlay for text readability */}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.4) 100%)' }} />

          {/* LIVE badge during meeting */}
          {showMeeting && (
            <div style={{ position: 'absolute', top: 10, left: 10, zIndex: 3, display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(4,120,87,0.9)', backdropFilter: 'blur(6px)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 20, padding: '4px 10px' }}>
              <span style={{ position: 'relative', display: 'inline-flex' }}>
                <span style={{ position: 'absolute', width: 8, height: 8, borderRadius: '50%', background: '#34D399', opacity: 0.7, animation: 'ping 1s infinite' }} />
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#34D399', display: 'inline-block' }} />
              </span>
              <span style={{ fontSize: 11, fontWeight: 800, color: '#A7F3D0', letterSpacing: 1.5 }}>LIVE</span>
            </div>
          )}

          {/* Cover edit button */}
          {canEditBrandingHub && !showMeeting && (
            <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6, zIndex: 2 }}>
              {coverImageUrl && (
                <button onClick={() => setIsCoverEditMode(v => !v)}
                  style={{ height: 28, padding: '0 10px', borderRadius: 9, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(4px)' }}>
                  {isCoverEditMode ? '✓ Klaar' : '↔ Positie'}
                </button>
              )}
              <button onClick={() => coverInputRef.current?.click()} disabled={savingCover}
                style={{ height: 28, padding: '0 10px', borderRadius: 9, background: 'rgba(0,0,0,0.5)', color: '#fff', border: 'none', fontSize: 11, fontWeight: 600, cursor: 'pointer', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Camera size={11} /> {savingCover ? '…' : 'Cover'}
              </button>
            </div>
          )}

          {/* Bottom bar on cover: logo + name + buttons */}
          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 12, zIndex: 2 }}>
            {/* Logo */}
            <div
              onClick={() => canEditBrandingHub && !showMeeting && !savingLogo && logoInputRef.current?.click()}
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: T.white,
                border: '2.5px solid rgba(255,255,255,0.9)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: savingLogo ? 11 : 18,
                fontWeight: 800,
                color: T.accent,
                cursor: canEditBrandingHub && !showMeeting && !savingLogo ? 'pointer' : 'default',
                flexShrink: 0,
                overflow: 'hidden',
                opacity: savingLogo ? 0.85 : 1,
              }}
              title={canEditBrandingHub && !showMeeting ? 'Klik om logo te wijzigen' : undefined}
            >
              {savingLogo ? (
                '…'
              ) : (club as any).logoUrl ? (
                <img src={(club as any).logoUrl} alt={club.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                logoInitial
              )}
            </div>
            {/* Name */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{club.name}</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>{typeLabel} · {rolLabel}</div>
            </div>
            {/* Header action buttons */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>

              {/* ── LIVE MEETING CONTROLS ── */}
              {showMeeting ? (
                <>
                  <button onClick={requestFullscreen}
                    style={{ ...S.headerBtn, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <FullscreenIcon size={13} weight="regular" /> Volledig
                  </button>
                  <button onClick={leaveMeeting}
                    style={{ ...S.headerBtn, background: '#EF4444', border: 'none', color: '#fff', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <EndCallIcon size={13} weight="regular" /> Verlaten
                  </button>
                </>
              ) : (
                <>
                  {/* Start Meeting — alleen voor organisator */}
                  {isMeetingHost && (
                    <button onClick={() => setShowPreMeeting(true)}
                      style={{ ...S.accentBtn, background: T.accent, boxShadow: '0 2px 8px rgba(141,168,160,0.35)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <VideoIcon size={14} weight="regular" /> Start Meeting
                    </button>
                  )}

                  {/* Join — voor leden als er een actieve meeting is */}
                  {!isMeetingHost && activeMeeting && (
                    <button onClick={() => { setMeetingRoom(activeMeeting.roomName); setShowMeeting(true); }}
                      style={{ ...S.accentBtn, background: T.accent, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <VideoIcon size={14} weight="regular" /> Joinen
                    </button>
                  )}

                  {canInviteMembersHub && (
                  <button onClick={() => setShowInviteModal(true)} style={S.headerBtn}>
                    + Uitnodigen
                  </button>
                  )}

                  {/* Next meeting button — always visible */}
                  <div ref={nextMeetingBtnRef} style={{ position: 'relative' }}>
                    <button
                      onClick={() => {
                        const rect = nextMeetingBtnRef.current?.getBoundingClientRect();
                        if (rect) setNextMeetingAnchor({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
                        setShowNextMeetingPopup(v => !v);
                      }}
                      style={{ ...S.headerBtn }}>
                      <CalendarIcon size={13} weight="regular" />
                      Next meeting
                      {nextEvent && (
                        <span style={{ background: T.accent, color: '#fff', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700, marginLeft: 2 }}>
                          {nextEvent.eventDate.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                    </button>
                  </div>

                  {/* Leden toggle */}
                  <button
                    onClick={() => setShowLedenPanel(v => !v)}
                    style={{ ...S.headerBtn, background: showLedenPanel ? T.accentBg : T.white, borderColor: showLedenPanel ? T.accent : T.border, color: showLedenPanel ? T.accent : T.text }}>
                    <Users size={13} strokeWidth={2} />
                    Leden
                  </button>

                  {/* Instellingen */}
                  <button
                    onClick={() => setActiveTab('instellingen')}
                    style={{ ...S.headerBtn, width: 34, height: 34, padding: 0, justifyContent: 'center' }}>
                    <PhosphorGear size={14} weight="regular" />
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── TABS ──────────────────────────────────────────────────────────── */}
        <div style={{ background: T.white, borderBottom: `1px solid ${T.border}` }}>
          <div style={{ display: 'flex', paddingLeft: 12 }}>
            {([
              { key: 'onsverhaal',   label: 'Ons verhaal',  Icon: ProfileIcon },
              { key: 'sessies',      label: 'Sessies',      Icon: FolderIcon },
              { key: 'groei',        label: 'Groei',        Icon: GrowthIcon },
              { key: 'agenda',       label: 'Agenda',       Icon: CalendarIcon },
              { key: 'instellingen', label: 'Instellingen', Icon: PhosphorGear },
            ] as const).map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '13px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === tab.key ? 700 : 500, color: activeTab === tab.key ? T.accent : T.textSub, borderBottom: activeTab === tab.key ? `2px solid ${T.accent}` : '2px solid transparent', marginBottom: -1, whiteSpace: 'nowrap' as const, transition: 'all 0.15s' }}>
                <tab.Icon size={14} weight="regular" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── MEETING COUNTDOWN (pre-meeting fullscreen overlay) ──────────── */}
      {showPreMeeting && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}>
          <MeetingCountdown
            entityId={activeEntity.id}
            clubName={club.name}
            logoUrl={(club as any)?.logoUrl ?? null}
            isHost={isMeetingHost || (preMeetingOpenTab === 'sessie' && canManageSessionsHub)}
            canOperateCountdown={isMeetingHost}
            restrictSettingsToSessionOnly={preMeetingSessionSetupOnly}
            primaryColor={T.accent}
            openSettings={preMeetingOpenTab}
            featureToggles={featureToggles}
            onFeatureToggle={handleFeatureToggle}
            sessionTemplateEditor={canManageSessionsHub ? {
              userId: user?.uid ?? '',
              entityType: (activeEntity.type as 'mastermind' | 'community' | 'networkclub') || 'mastermind',
              memberCount: members.length,
              memberNames: members.map(m => m.name),
              memberAvatars: members.map(m => m.photoURL ?? null),
            } : null}
            onMeetingStart={() => { setShowPreMeeting(false); setPreMeetingOpenTab(undefined); void startMeeting(); }}
            onClose={() => { setShowPreMeeting(false); setPreMeetingOpenTab(undefined); }}
          />
        </div>
      )}

      {/* Leden (en iedereen buiten pre-meeting): live meekijken met countdown zodra host gestart is */}
      {showCountdownViewer && !showPreMeeting && activeEntity && club && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500 }}>
          <MeetingCountdown
            entityId={activeEntity.id}
            clubName={club.name}
            logoUrl={(club as any)?.logoUrl ?? null}
            isHost={false}
            primaryColor={T.accent}
            featureToggles={featureToggles}
            sessionTemplateEditor={null}
            onMeetingStart={() => {
              setShowCountdownViewer(false);
              const rn = activeMeeting?.roomName;
              if (rn) {
                setMeetingRoom(rn);
                setShowMeeting(true);
              }
            }}
            onClose={() => setShowCountdownViewer(false)}
          />
        </div>
      )}

      {/* Banner: countdown loopt, je zit nog niet in Jitsi / geen host-setup open */}
      {countdownRunningRemote && !showMeeting && !showPreMeeting && !showCountdownViewer && accessOk && activeEntity && club && (
        <div
          style={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 460,
            maxWidth: 420,
            width: 'calc(100% - 32px)',
            padding: '14px 18px',
            borderRadius: 16,
            background: 'linear-gradient(135deg, #064E3B 0%, #065F46 100%)',
            border: '1px solid rgba(52, 211, 153, 0.35)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: '#6EE7B7', textTransform: 'uppercase', marginBottom: 4 }}>Countdown bezig</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', lineHeight: 1.35 }}>
              De meeting start met een countdown. Iedereen kan live meekijken — ook voordat je de video joint.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowCountdownViewer(true)}
            style={{
              flexShrink: 0,
              padding: '10px 16px',
              borderRadius: 12,
              border: 'none',
              background: '#10B981',
              color: '#fff',
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            Meekijken
          </button>
        </div>
      )}

      {/* ── POST-SESSION REFLECTIE ───────────────────────────────────────────── */}
      {showPostSession && completedMeetingId && user && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(8px)' }}>
          <div style={{ ...S.card, width: '100%', maxWidth: 520, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 14px', borderBottom: `1px solid ${T.border}` }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>Reflectie na sessie</div>
                <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{club.name}</div>
              </div>
              <button onClick={() => setShowPostSession(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, padding: 4, borderRadius: 8 }}>
                <X size={15} strokeWidth={2} />
              </button>
            </div>
            <div style={{ padding: 20 }}>
              <PostSessionModule
                entityId={activeEntity.id}
                entityType={(activeEntity.type as StructuredGrowthEntityType) || 'mastermind'}
                userId={user.uid}
                userName={user.displayName || user.email?.split('@')[0] || 'Lid'}
                meetingId={completedMeetingId}
                onSubmitted={() => { setShowPostSession(false); loadSessions(activeEntity.id, sessionsYear); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── NEXT MEETING POPUP — fixed, buiten overflow:hidden containers ── */}
      {showNextMeetingPopup && nextMeetingAnchor && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 300 }} onClick={() => setShowNextMeetingPopup(false)}>
          <div style={{ position: 'absolute', top: nextMeetingAnchor.top, right: nextMeetingAnchor.right }} onClick={e => e.stopPropagation()}>
            <NextMeetingPopup
              event={nextEvent}
              members={members}
              userId={user?.uid || ''}
              entityId={activeEntity.id}
              userDisplayName={user?.displayName || user?.email?.split('@')[0] || 'Lid'}
              userPhotoURL={user?.photoURL ?? null}
              isAdmin={isNextMeetingAdmin}
              onClose={() => setShowNextMeetingPopup(false)}
              onPlanMeeting={() => { setShowNextMeetingPopup(false); setActiveTab('agenda'); }}
              onJoinMeeting={() => {
                setShowNextMeetingPopup(false);
                const rn = (nextEvent as any)?.roomName || activeMeeting?.roomName;
                if (rn) setMeetingRoom(rn);
                setShowMeeting(true);
              }}
            />
          </div>
        </div>
      )}

      {/* ── INVITE MODAL ───────────────────────────────────────────────────── */}
      {showInviteModal && (
        <ClubInviteMembersModal
          clubName={club.name}
          entityId={activeEntity.id}
          clubId={club.id}
          userId={user?.uid ?? ''}
          userName={user?.displayName || user?.email || 'Lid'}
          userRole={activeEntity?.role}
          entityType={activeEntity.type}
          members={members}
          isOwner={isOwner}
          isBoard={isBoard}
          canInviteMembersHub={canInviteMembersHub}
          accentColor={T.accent}
          onMembersChanged={() => { if (activeEntity?.id) void loadData(activeEntity.id); }}
          onClose={() => setShowInviteModal(false)}
        />
      )}

      {/* ── TAB CONTENT ── */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden', background: T.bg }}>

        {/* TERUGKNOP — alleen zichtbaar als een subview open is */}
        {activeTab === 'onsverhaal' && clubLandingView !== 'overview' && (
          <div style={{ flexShrink: 0, padding: '8px 14px 0', background: T.bg }}>
            <button type="button" onClick={() => setClubLandingView('overview')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 9, border: `1.5px solid ${T.accent}44`, background: T.white, color: T.accent, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
              ← Terug
            </button>
          </div>
        )}

        {/* ONS VERHAAL FULL-WIDTH VIEW */}
        {activeTab === 'onsverhaal' && clubLandingView === 'story' && (
          <div style={{ flex: 1, minHeight: 0, padding: '8px 14px 12px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, background: '#F9FAFB', borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Nav bar */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '8px 14px', borderBottom: `1px solid ${T.border}`, background: T.bg, flexShrink: 0 }}>
                <div style={{ fontSize: 12, color: T.textSub, minWidth: 0 }}>
                  <span style={{ fontWeight: 700, color: T.text }}>Ons verhaal van {club.name}</span>
                  <span style={{ display: 'block', marginTop: 1, fontSize: 11 }}>{typeLabel} · SuperKonnected</span>
                </div>
                {(isOwner || isBoard) && (
                  <button type="button" onClick={() => setActiveTab('instellingen' as any)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9, border: `1.5px solid ${T.accent}44`, background: T.white, color: T.accent, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                    ✎ Verhaal bewerken
                  </button>
                )}
              </div>
              {/* Scrollable content */}
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {/* HERO */}
                <div style={{ position: 'relative', minHeight: 240, background: `linear-gradient(135deg, #1B3A4B 0%, ${T.accent} 100%)`, overflow: 'hidden' }}>
                  {coverImageUrl && (
                    <img src={coverImageUrl} alt="" aria-hidden="true"
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `${coverPosition.x}% ${coverPosition.y}%`, opacity: 0.38 }} />
                  )}
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 60%, transparent 100%)' }} />
                  <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '36px 20px 60px' }}>
                    <div style={{ width: 80, height: 80, borderRadius: '50%', border: '4px solid rgba(255,255,255,0.9)', background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', overflow: 'hidden', marginBottom: 16 }}>
                      {(club as any).logoUrl ? (
                        <img src={(club as any).logoUrl} alt={`Logo ${club.name}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: `linear-gradient(135deg, #1B3A4B, ${T.accent})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <span style={{ fontSize: 28, fontWeight: 900, color: '#fff' }}>{club.name.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                    <h1 style={{ fontSize: 26, fontWeight: 900, color: '#fff', margin: '0 0 8px', textAlign: 'center', lineHeight: 1.15, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>{club.name}</h1>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center', marginBottom: 18 }}>
                      <span style={{ background: 'rgba(255,255,255,0.18)', color: '#fff', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700, border: '1px solid rgba(255,255,255,0.25)' }}>{typeLabel}</span>
                      {(club as any).categories?.slice(0, 2).map((cat: string, i: number) => (
                        <span key={i} style={{ background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', borderRadius: 20, padding: '3px 9px', fontSize: 11.5, fontWeight: 600 }}>{cat}</span>
                      ))}
                    </div>
                    {clubWebsiteUrl && (
                      <button type="button" onClick={() => void openExternalUrl(clubWebsiteUrl)}
                        style={{ padding: '8px 16px', borderRadius: 10, border: 'none', background: '#fff', color: T.accent, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                        🌐 Website
                      </button>
                    )}
                  </div>
                </div>
                {/* White content */}
                <div style={{ maxWidth: 740, margin: '0 auto', padding: '28px 20px 48px', display: 'flex', flexDirection: 'column', gap: 22 }}>
                  {(() => {
                    const clubDesc: string = (club as any).description || '';
                    const clubVals: string[] = Array.isArray((club as any).values) ? (club as any).values
                      : Array.isArray((club as any).categories) ? (club as any).categories.slice(0, 3) : [];
                    const founder = members.find(m => m.role === 'owner') || members.find(m => m.role === 'organizer');
                    return (
                      <>
                        <section>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                            <div style={{ width: 4, height: 24, borderRadius: 4, background: `linear-gradient(to bottom, ${T.accent}, #1B3A4B)` }} />
                            <h2 style={{ fontSize: 18, fontWeight: 900, color: T.text, margin: 0 }}>Ons verhaal</h2>
                          </div>
                          {clubVals.length > 0 && (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 18 }}>
                              {clubVals.slice(0, 3).map((val: string, i: number) => (
                                <div key={i} style={{ background: T.white, borderRadius: 10, padding: '14px 16px', border: `1px solid ${T.border}` }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 4 }}>Waarde {i + 1}</div>
                                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{val}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          <div style={{ background: T.white, borderRadius: 12, padding: '20px 22px', border: `1px solid ${T.border}` }}>
                            {clubDesc ? (
                              <p style={{ fontSize: 14.5, color: T.text, lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>{clubDesc}</p>
                            ) : (
                              <p style={{ fontSize: 13.5, color: T.textMuted, fontStyle: 'italic', margin: 0, lineHeight: 1.7 }}>
                                {(isOwner || isBoard) ? 'Voeg jullie verhaal toe via Instellingen → Clubverhaal.' : 'Nog geen verhaal toegevoegd.'}
                              </p>
                            )}
                          </div>
                        </section>
                        {founder && (
                          <section>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                              <div style={{ width: 4, height: 24, borderRadius: 4, background: `linear-gradient(to bottom, ${T.accent}, #1B3A4B)` }} />
                              <h2 style={{ fontSize: 18, fontWeight: 900, color: T.text, margin: 0 }}>Ontmoet de Founder</h2>
                            </div>
                            <div style={{ background: T.white, borderRadius: 12, padding: '20px 22px', border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                              <div style={{ width: 60, height: 60, borderRadius: '50%', overflow: 'hidden', flexShrink: 0, border: `3px solid ${T.accent}44`, background: T.bg }}>
                                {founder.photoURL ? (
                                  <img src={founder.photoURL} alt={founder.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${T.accent}22`, color: T.accent, fontSize: 22, fontWeight: 800 }}>
                                    {founder.name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{ fontSize: 16, fontWeight: 800, color: T.text, margin: '0 0 2px' }}>{founder.name}</p>
                                <button type="button"
                                  onClick={() => { window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId: founder.id } })); window.location.hash = '#mijn-verhaal'; }}
                                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, border: `1.5px solid ${T.accent}44`, background: 'transparent', color: T.accent, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', marginTop: 8 }}>
                                  Bekijk persoonlijk verhaal →
                                </button>
                              </div>
                            </div>
                          </section>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div style={{ padding: '6px 14px', fontSize: 10.5, color: T.textMuted, borderTop: `1px solid ${T.border}`, flexShrink: 0, background: T.bg }}>
                Slug: superkonnected.com/clubs/{generateClubSlug(club.name)}
              </div>
            </div>
          </div>
        )}

        {/* WEBSITE FULL-WIDTH VIEW */}
        {activeTab === 'onsverhaal' && clubLandingView === 'website' && clubWebsiteUrl && (
          <div style={{ flex: 1, minHeight: 0, padding: '8px 14px 12px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: 1, minHeight: 0, background: T.white, borderRadius: 10, border: `1px solid ${T.border}`, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '8px 14px', borderBottom: `1px solid ${T.border}`, background: T.bg, flexShrink: 0 }}>
                <div style={{ fontSize: 11.5, color: T.textSub, minWidth: 0 }}>
                  <span style={{ fontWeight: 700, color: T.text }}>Website van {club.name}</span>
                  <span style={{ display: 'block', marginTop: 1, wordBreak: 'break-all' }}>{clubWebsiteUrl}</span>
                </div>
                <button type="button" onClick={() => void openExternalUrl(clubWebsiteUrl)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 9, border: `1.5px solid ${T.accent}44`, background: T.white, color: T.accent, fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}>
                  <LaunchIcon size={14} weight="regular" />
                  {Capacitor.isNativePlatform() ? 'Open in app-browser' : 'Open in nieuw tabblad'}
                </button>
              </div>
              {Capacitor.isNativePlatform() ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, textAlign: 'center', padding: '24px 18px' }}>
                  <p style={{ fontSize: 13, color: T.textSub, margin: 0, maxWidth: 360, lineHeight: 1.5 }}>Op mobiel openen we externe websites in de ingebouwde browser.</p>
                  <button type="button" onClick={() => void openExternalUrl(clubWebsiteUrl)}
                    style={{ padding: '10px 20px', borderRadius: 10, border: 'none', background: T.accent, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Website openen
                  </button>
                </div>
              ) : (
                <iframe title={`Website ${club.name}`} src={clubWebsiteUrl}
                  style={{ flex: 1, width: '100%', border: 'none', display: 'block', minHeight: 0 }}
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
                  referrerPolicy="no-referrer-when-downgrade" />
              )}
              <div style={{ padding: '6px 14px', fontSize: 10.5, color: T.textMuted, borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
                {Capacitor.isNativePlatform() ? 'Sluit de browser om terug te keren.' : 'Sommige sites blokkeren insluiting. Gebruik dan "Open in nieuw tabblad".'}
              </div>
            </div>
          </div>
        )}

        {/* SESSIE-PANEL + TABS — verborgen bij story- en website-view */}
        {!(activeTab === 'onsverhaal' && (clubLandingView === 'story' || (clubLandingView === 'website' && clubWebsiteUrl))) && (
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

        {/* LEFT: permanente sessie-navigator — zichtbaar bij alle tabs */}
        <div style={{ width: 170, flexShrink: 0, background: T.white, borderRight: `1px solid ${T.border}`, overflowY: 'auto', padding: '14px 0' }}>
          <div style={{ padding: '0 14px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>Sessies</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button type="button" onClick={() => setSessionsYear(y => y - 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 13, padding: '0 2px', lineHeight: 1 }} aria-label="Vorig jaar">{'<'}</button>
              <span style={{ fontSize: 12, fontWeight: 700, color: T.text }}>{sessionsYear}</span>
              <button type="button" onClick={() => setSessionsYear(y => y + 1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMuted, fontSize: 13, padding: '0 2px', lineHeight: 1 }} aria-label="Volgend jaar">{'>'}</button>
            </div>
          </div>
          {canManageSessionsHub && (
            <div style={{ margin: '0 10px 12px', padding: '10px 12px', borderRadius: 10, background: T.white, border: `1px solid ${T.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                <AssistantIcon size={13} weight="regular" color={T.accent} />
                <span style={{ fontSize: 11, fontWeight: 700, color: T.accent, textTransform: 'uppercase', letterSpacing: 0.5 }}>AI Notes</span>
              </div>
              <div style={{ fontSize: 11.5, color: T.textSub, lineHeight: 1.4 }}>Automatisch gegenereerde notities na elke sessie</div>
            </div>
          )}
          {sessionsLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 24 }}>
              <Loader size={16} strokeWidth={2} className="animate-spin" style={{ color: T.textMuted }} />
            </div>
          ) : (
            MONTHS.map(month => {
              const monthSessions = sessionsByMonth[month] || [];
              const isActiveMonth = monthSessions.length > 0;
              return (
                <div key={month}>
                  <div style={{ padding: '6px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11.5, fontWeight: isActiveMonth ? 700 : 500, color: isActiveMonth ? T.text : T.textMuted }}>{month}</span>
                    {isActiveMonth && <span style={{ fontSize: 10, fontWeight: 800, background: T.accent, color: '#fff', borderRadius: 20, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>{monthSessions.length}</span>}
                  </div>
                  {isActiveMonth && (
                    <div style={{ padding: '2px 14px 6px' }}>
                      <div style={{ fontSize: 10.5, color: T.textMuted, fontWeight: 600, paddingLeft: 6, borderLeft: `2px solid ${T.accentLight}`, marginBottom: 6 }}>Meeting</div>
                      {monthSessions.map(s => {
                        const isSel = selectedSession?.id === s.id;
                        const d = s.createdAt;
                        const dayStr = `${d.getDate()} ${d.toLocaleDateString('nl-NL', { month: 'short' }).toLowerCase()} \u00B7 ${formatDuration(s.durationSeconds)}`;
                        return (
                          <button key={s.id}
                            onClick={() => { setSelectedSession(isSel ? null : s); setActiveTab('sessies'); }}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6, padding: '5px 6px', borderRadius: 7, background: isSel ? T.accentBg : 'none', border: isSel ? `1px solid ${T.accent}` : '1px solid transparent', cursor: 'pointer', marginBottom: 3, textAlign: 'left' }}>
                            <ClockIcon size={12} weight="regular" color={T.textMuted} />
                            <span style={{ fontSize: 11.5, color: isSel ? T.accent : T.text, fontWeight: isSel ? 700 : 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dayStr}</span>
                            {(s.participants || []).length > 0 && <span style={{ fontSize: 9.5, background: T.bg, color: T.textSub, borderRadius: 10, padding: '1px 4px', border: `1px solid ${T.border}` }}>{(s.participants || []).length}</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* CENTER: live meeting altijd hier (onafhankelijk van tab); anders tab-inhoud */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', background: T.bg, minHeight: 0 }}>

          {showMeeting && meetingRoom ? (
            <div id="mm-meeting-container" style={{ flex: 1, overflow: 'hidden', position: 'relative', minHeight: 0 }}>
              <NetworkClubMeeting
                roomName={meetingRoom}
                meetingTitle={`${club.name} \u2014 Mastermind`}
                clubName={club.name}
                entityId={activeEntity.id}
                entityType={activeEntity.type as any}
                isHost={isMeetingHost}
                autoOpenPresentation={false}
                clubLogoUrl={(club as any)?.logoUrl}
                overlaySettings={(club as any)?.presentationOverlaySettings || {}}
                presentationColors={{ primary: T.accent, secondary: '#1B3A4B', accent: T.accent }}
                memberNames={members.map(m => m.name)}
                memberUids={members.map(m => m.id)}
                memberCount={members.length}
                memberAvatars={members.map(m => m.photoURL ?? null)}
                memberMeta={members.map(m => ({ name: m.name, avatar: m.photoURL ?? null, country: m.country, timezone: m.timezone }))}
                onLeave={leaveMeeting}
              />
            </div>
          ) : (
            <>
          {/* ONS VERHAAL — alleen story-view (website-view staat fullwidth boven het sessie-panel) */}
          {activeTab === 'onsverhaal' && clubLandingView !== 'website' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 900, margin: '0 auto', width: '100%' }}>
                {(() => {
                  const clubDescription = (club as any).description as string | undefined;
                  const founderM = members.find(m => m.role === 'owner') || members.find(m => m.role === 'organizer');
                  return (
                    <div style={{ ...S.card, overflow: 'hidden', padding: 0 }}>
                      {/* Hero */}
                      <div
                        style={{
                          position: 'relative',
                          height: 210,
                          background: `linear-gradient(135deg, #1B3A4B 0%, ${T.accent} 100%)`,
                        }}
                      >
                        {coverImageUrl && (
                          <img
                            src={coverImageUrl}
                            alt=""
                            draggable={false}
                            style={{
                              position: 'absolute',
                              inset: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              objectPosition: `${coverPosition.x}% ${coverPosition.y}%`,
                              opacity: 0.35,
                            }}
                          />
                        )}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.45), rgba(0,0,0,0.1))' }} />
                        <div style={{ position: 'absolute', left: 18, bottom: 16, right: 18, color: '#fff' }}>
                          <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15 }}>{club.name}</div>
                          <div style={{ marginTop: 6, fontSize: 13, color: 'rgba(255,255,255,0.9)' }}>
                            {typeLabel} · SuperKonnected
                          </div>
                        </div>
                      </div>

                      {/* Club beschrijving */}
                      <div style={{ padding: '16px 18px' }}>
                        {clubDescription ? (
                          <p style={{ fontSize: 13.5, color: T.text, lineHeight: 1.7, margin: 0 }}>{clubDescription}</p>
                        ) : (
                          <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, margin: 0, fontStyle: 'italic' }}>
                            {(isOwner || isBoard)
                              ? 'Voeg het verhaal en de filosofie van deze groep toe via Instellingen → Clubverhaal. Leden zien hier dan wie jullie zijn en waar jullie voor staan.'
                              : 'Nog geen verhaal toegevoegd.'}
                          </p>
                        )}
                      </div>

                      {/* Knoppen: Ons verhaal (volledige pagina) + Verhaal van de Founder */}
                      <div style={{ padding: '0 18px 18px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => setClubLandingView('story')}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: 'none', background: T.accent, color: '#fff', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                        >
                          Ons verhaal
                        </button>
                        {clubWebsiteUrl && (
                          <button
                            type="button"
                            onClick={() => setClubLandingView('website')}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: `1.5px solid ${T.accent}44`, background: 'transparent', color: T.accent, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                          >
                            🌐 Onze website
                          </button>
                        )}
                        {founderM && (
                          <button
                            type="button"
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('openUserProfile', { detail: { userId: founderM.id } }));
                              window.location.hash = '#mijn-verhaal';
                            }}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 10, border: `1.5px solid ${T.accent}44`, background: 'transparent', color: T.accent, fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}
                          >
                            {founderM.photoURL && (
                              <img src={founderM.photoURL} alt="" style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                            )}
                            Verhaal van de Founder
                          </button>
                        )}
                      </div>

                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* SESSIES */}
          {activeTab === 'sessies' && (
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {selectedSession ? (
                    <SessionDetail session={selectedSession} userId={user?.uid || ''} isHost={canManageSessionsHub} onBack={() => setSelectedSession(null)} />
                  ) : (
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: T.textMuted, gap: 12 }}>
                      {countdownRunningRemote && !activeMeeting && accessOk && (
                        <div style={{ ...S.card, padding: '16px 20px', maxWidth: 420, width: '100%', marginBottom: 16, background: 'linear-gradient(135deg,#064E3B,#065F46)', border: '1px solid rgba(52,211,153,0.35)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(52,211,153,0.2)', border: '2px solid rgba(52,211,153,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>
                              ⏱️
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: '#6EE7B7', textTransform: 'uppercase', marginBottom: 2 }}>Meeting start zo!</div>
                              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>Live countdown actief</div>
                              <div style={{ fontSize: 11.5, color: 'rgba(167,243,208,0.85)' }}>Stap nu in en wacht mee tot de start</div>
                            </div>
                            <button
                              onClick={() => setShowCountdownViewer(true)}
                              style={{ height: 38, padding: '0 18px', borderRadius: 10, background: '#10B981', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                            >
                              ▶ Meekijken
                            </button>
                          </div>
                        </div>
                      )}
                      {activeMeeting && (
                        <div style={{ ...S.card, padding: '16px 20px', maxWidth: 420, width: '100%', marginBottom: 16, background: 'linear-gradient(135deg,#064E3B,#065F46)', border: '1px solid rgba(52,211,153,0.25)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                            <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(52,211,153,0.2)', border: '2px solid rgba(52,211,153,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <MicrophoneIcon size={22} weight="regular" color="#6EE7B7" />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.5, color: '#6EE7B7', textTransform: 'uppercase', marginBottom: 2 }}>Nu live</div>
                              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>Online meeting actief</div>
                              <div style={{ fontSize: 11.5, color: 'rgba(167,243,208,0.85)' }}>door {activeMeeting.startedByName}</div>
                            </div>
                            <button onClick={() => { setMeetingRoom(activeMeeting.roomName); setShowMeeting(true); }}
                              style={{ height: 38, padding: '0 18px', borderRadius: 10, background: '#10B981', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                              Joinen
                            </button>
                          </div>
                        </div>
                      )}
                      <div style={{ marginBottom: 4 }}><FolderIcon size={36} weight="light" color={T.textMuted} /></div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: T.textSub }}>Selecteer een sessie</div>
                      <div style={{ fontSize: 13, color: T.textMuted }}>Kies een opname in de lijst links om details te bekijken</div>
                      {(canManageSessionsHub || isMeetingHost) && (
                        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                          {canManageSessionsHub && (
                            <button onClick={() => { setPreMeetingOpenTab('sessie'); setShowPreMeeting(true); }} style={{ ...S.headerBtn, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                              <ClipboardIcon size={13} weight="regular" /> Sessie configureren
                            </button>
                          )}
                          {isMeetingHost && (
                            <button onClick={() => { setPreMeetingOpenTab(undefined); setShowPreMeeting(true); }} style={{ ...S.accentBtn, fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
                              <MicrophoneIcon size={13} weight="regular" /> Start meeting
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
            </div>
          )}

          {/* GROEI */}
          {activeTab === 'groei' && user && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              <div style={{ maxWidth: 740, margin: '0 auto' }}>
                <div style={{ ...S.card, padding: '18px 20px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <GrowthIcon size={22} weight="regular" color={T.accent} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 3, letterSpacing: -0.2 }}>
                        Structured Growth Engine
                      </div>
                      <div style={{ fontSize: 12.5, color: T.textSub, lineHeight: 1.55 }}>
                        Jouw groei-cockpit voor deze Mastermind. Voorbereiding, doelen, afspraken en groepsactiviteit.
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 14 }}>
                    {([
                      { Icon: CheckIcon,    color: T.accent,  label: 'Voorbereiding', sub: `Vul in v\u00f3\u00f3r elke sessie`, action: 'prep' as const },
                      { Icon: GoalIcon,     color: T.accent,  label: 'Hoofddoel',     sub: 'Wat wil de groep bereiken?', action: 'goal' as const },
                      { Icon: TimeIcon,     color: '#5B8EF0', label: 'Sessietimer',   sub: 'Open tab Instellingen (countdown)', action: 'timer' as const },
                      { Icon: RelationIcon, color: '#9B6E3D', label: 'Commitments',   sub: 'Afspraken nakomen', action: 'commitment' as const },
                    ] as const).map(item => {
                      const hovered = growthCardHover === item.label;
                      return (
                        <button
                          key={item.label}
                          type="button"
                          onClick={() => {
                            if (item.action === 'timer') setActiveTab('instellingen');
                            else if (item.action === 'prep') scrollToGrowthSection('mm-growth-prep');
                            else if (item.action === 'goal') scrollToGrowthSection('mm-growth-goal');
                            else if (item.action === 'commitment') scrollToGrowthSection('mm-growth-commitment');
                          }}
                          onMouseEnter={() => setGrowthCardHover(item.label)}
                          onMouseLeave={() => setGrowthCardHover(null)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '12px 14px',
                            borderRadius: 12,
                            background: T.white,
                            border: `1.5px solid ${hovered ? item.color : T.border}`,
                            cursor: 'pointer',
                            textAlign: 'left',
                            width: '100%',
                            font: 'inherit',
                            color: 'inherit',
                            boxShadow: hovered ? '0 6px 20px rgba(0,0,0,0.08)' : T.shadowSm,
                            transform: hovered ? 'translateY(-1px)' : 'none',
                            transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
                          }}
                        >
                          <span style={{ flexShrink: 0, width: 36, height: 36, borderRadius: 10, background: `${item.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <item.Icon size={18} weight="duotone" color={item.color} />
                          </span>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: item.color }}>{item.label}</div>
                            <div style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.35 }}>{item.sub}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div id="mm-growth-prep">
                  <PreSessionModule entityId={activeEntity.id} entityType={(activeEntity.type as StructuredGrowthEntityType) || 'mastermind'} userId={user.uid} />
                </div>
                <div id="mm-growth-goal" style={{ marginTop: 16 }}>
                  <GoalEngine entityId={activeEntity.id} entityType={(activeEntity.type as StructuredGrowthEntityType) || 'mastermind'} isAdmin={canManageGrowthHub} userId={user.uid} />
                </div>
                <div id="mm-growth-commitment" style={{ marginTop: 16 }}>
                  <CommitmentEngine entityId={activeEntity.id} entityType={(activeEntity.type as StructuredGrowthEntityType) || 'mastermind'} userId={user.uid} userName={user.displayName || user.email?.split('@')[0] || 'Lid'} meetingId={`session-${activeEntity.id}`} />
                </div>
                <div id="mm-growth-health" style={{ marginTop: 16, marginBottom: 24 }}>
                  <GroupHealthIndicator entityId={activeEntity.id} entityType={(activeEntity.type as StructuredGrowthEntityType) || 'mastermind'} />
                </div>
              </div>
            </div>
          )}

          {/* AGENDA */}
          {activeTab === 'agenda' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              <div style={{ maxWidth: 820, margin: '0 auto' }}>

                {/* ── Live countdown banner op agenda-tab ─────────────────── */}
                {countdownRunningRemote && !showMeeting && accessOk && (
                  <div
                    style={{
                      marginBottom: 18,
                      padding: '18px 20px',
                      borderRadius: 16,
                      background: 'linear-gradient(135deg, #064E3B 0%, #065F46 100%)',
                      border: '1px solid rgba(52, 211, 153, 0.35)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                    }}
                  >
                    <div style={{ fontSize: 28 }}>⏱️</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.2, color: '#6EE7B7', textTransform: 'uppercase', marginBottom: 4 }}>
                        Meeting start zo!
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', lineHeight: 1.4 }}>
                        Er loopt een live countdown voor deze groep. Klik op <strong>Meekijken</strong> om in te stappen.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowCountdownViewer(true)}
                      style={{
                        flexShrink: 0,
                        padding: '10px 18px',
                        borderRadius: 12,
                        border: 'none',
                        background: '#10B981',
                        color: '#fff',
                        fontSize: 14,
                        fontWeight: 800,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ▶ Meekijken
                    </button>
                  </div>
                )}

                <div style={{ ...S.card, padding: '20px 24px' }}>
                  <ClubMeetingPlanner
                    entityId={activeEntity.id}
                    clubName={club.name}
                    currentUserId={user?.uid || ''}
                    currentUserName={user?.displayName || user?.email?.split('@')[0] || 'Organisator'}
                    memberUids={members.map(m => m.id)}
                    canPlan={canManageAgendaHub}
                    primaryColor={T.accent}
                    accentColor={T.accent}
                  />
                </div>
              </div>
            </div>
          )}

          {/* INSTELLINGEN */}
          {activeTab === 'instellingen' && (
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
              <div style={{ maxWidth: 820, margin: '0 auto' }}>
                <div style={{ ...S.card, padding: '20px 22px', marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Informatie</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                    {[
                      { label: 'Naam', value: club.name },
                      { label: 'Type', value: typeLabel },
                      { label: 'Leden', value: String(memberCount) },
                      { label: 'Meetings', value: String(sessions.length) },
                    ].map(row => (
                      <div key={row.label}>
                        <div style={{ fontSize: 11.5, color: T.textMuted, marginBottom: 2 }}>{row.label}</div>
                        <div style={{ fontSize: 14, fontWeight: 700, color: T.accent }}>{row.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ ...S.card, padding: '20px 22px' }}>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 14, lineHeight: 1.55 }}>
                    Als je de groep verlaat moet je opnieuw uitgenodigd worden om toegang te krijgen.
                  </div>
                  <button
                    onClick={async () => {
                      if (!user?.uid || !activeEntity) return;
                      if (!window.confirm('Weet je zeker dat je de groep wilt verlaten?')) return;
                      const snap = await getDocs(query(collection(db, 'entityMembers'), where('entityId', '==', activeEntity.id), where('userId', '==', user.uid)));
                      if (!snap.empty) await deleteDoc(snap.docs[0].ref);
                    }}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10, border: '1.5px solid #FECACA', background: '#FFF5F5', color: '#EF4444', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    <DeleteIcon size={14} weight="regular" /> Groep verlaten
                  </button>
                </div>
                {canEditBrandingHub && (
                  <div style={{ ...S.card, padding: '20px 22px', marginTop: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Website van de groep</div>
                    <div style={{ fontSize: 12, color: T.textSub, marginBottom: 10, lineHeight: 1.5 }}>
                      Zichtbaar op het tabblad <strong>Ons verhaal</strong> als &quot;Onze website&quot; (insluiten op web, app-browser op mobiel).
                    </div>
                    <input
                      type="url"
                      value={websiteInput}
                      onChange={(e) => setWebsiteInput(e.target.value)}
                      placeholder="https://jouworganisatie.nl"
                      style={{
                        width: '100%',
                        height: 40,
                        padding: '0 12px',
                        borderRadius: 10,
                        border: `1px solid ${T.border}`,
                        fontSize: 13,
                        boxSizing: 'border-box',
                        marginBottom: 8,
                      }}
                    />
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10 }}>
                      Leeg laten om te verwijderen.
                    </div>
                    {websiteError && (
                      <div style={{ marginBottom: 10, padding: '8px 12px', background: '#FEF2F2', borderRadius: 8, color: '#B91C1C', fontSize: 12, fontWeight: 600 }}>
                        {websiteError}
                      </div>
                    )}
                    {websiteSaved && (
                      <div style={{ marginBottom: 10, padding: '8px 14px', background: '#ECFDF5', borderRadius: 8, color: '#065F46', fontSize: 12, fontWeight: 600, border: '1px solid #A7F3D0' }}>
                        ✓ Website opgeslagen
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => void saveClubWebsite()}
                      disabled={websiteSaving}
                      style={{ ...S.accentBtn, opacity: websiteSaving ? 0.65 : 1, cursor: websiteSaving ? 'default' : 'pointer' }}
                    >
                      {websiteSaving ? 'Opslaan…' : 'Website opslaan'}
                    </button>
                  </div>
                )}
                {canEditBrandingHub && (
                  <div style={{ ...S.card, padding: '20px 22px', marginTop: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 14 }}>Cover & Logo</div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        type="button"
                        disabled={savingCover}
                        onClick={() => !savingCover && coverInputRef.current?.click()}
                        style={{ ...S.headerBtn, fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, opacity: savingCover ? 0.6 : 1, cursor: savingCover ? 'default' : 'pointer' }}
                      >
                        <ImageIcon size={13} weight="regular" /> {savingCover ? 'Cover…' : 'Cover wijzigen'}
                      </button>
                      <button
                        type="button"
                        disabled={savingLogo}
                        onClick={() => !savingLogo && logoInputRef.current?.click()}
                        style={{ ...S.headerBtn, fontSize: 13, display: 'flex', alignItems: 'center', gap: 5, opacity: savingLogo ? 0.6 : 1, cursor: savingLogo ? 'default' : 'pointer' }}
                      >
                        <TagIcon size={13} weight="regular" /> {savingLogo ? 'Logo…' : 'Logo wijzigen'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
            </>
          )}
        </div>

        {/* RIGHT: permanent ledenvenster — zichtbaar bij alle tabs */}
        {showLedenPanel && (
          <div style={{ width: 280, flexShrink: 0, background: T.white, borderLeft: `1px solid ${T.border}`, overflowY: 'auto', padding: '16px 0' }}>
            <div style={{ padding: '0 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Users size={13} strokeWidth={2} color={T.textMuted} />
                <span style={{ fontSize: 12, fontWeight: 800, color: T.text, textTransform: 'uppercase', letterSpacing: 0.5 }}>Leden {memberCount}</span>
              </div>
              {canInviteMembersHub && (
                <button onClick={() => setShowInviteModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: T.textSub, padding: '2px 4px' }}>
                  + Uitnodigen
                </button>
              )}
            </div>
            <div style={{ padding: '0 16px 4px', fontSize: 10.5, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 5, marginBottom: 8 }}>
              <ClockIcon size={11} weight="regular" color={T.textMuted} />
              {'Timezone\u00A0\u00B7\u00A0Lokale tijd'}
            </div>
            {members.map(m => {
              const initials = m.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
              const flag = m.country ? COUNTRY_FLAGS[m.country] || '' : '';
              const tz = m.timezone || getGmtOffset();
              const timeStr = `${new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' })} ${tz}`;
              const isOrgOrBoard = m.role === 'owner' || m.role === 'organizer' || m.role === 'board';
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: `1px solid ${T.borderLight}` }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    {m.photoURL
                      ? <img src={m.photoURL} alt={m.name} style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      : <div style={{ width: 36, height: 36, borderRadius: '50%', background: T.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: T.accent }}>{initials}</div>
                    }
                    <span style={{ position: 'absolute', bottom: 0, right: 0, width: 9, height: 9, borderRadius: '50%', background: '#10B981', border: `1.5px solid ${T.white}` }} />
                    {flag && <span style={{ position: 'absolute', top: -2, right: -4, fontSize: 10 }}>{flag}</span>}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                    <div style={{ fontSize: 11, color: T.textMuted }}>{timeStr}</div>
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 700, background: isOrgOrBoard ? T.badgeDark : T.badgeLight, color: isOrgOrBoard ? '#fff' : T.textSub, borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap' as const, flexShrink: 0 }}>
                    {m.role === 'owner' ? 'Eigenaar' : m.role === 'board' ? 'Bestuur' : m.role === 'organizer' ? 'Beheerder' : 'Lid'}
                  </span>
                </div>
              );
            })}
            {members.length === 0 && <div style={{ padding: '16px', fontSize: 12.5, color: T.textMuted, textAlign: 'center' }}>Nog geen leden</div>}
          </div>
        )}
        </div>
        )}
      </div>
    </div>
  );
};

export default MastermindCommunityHub;
